const en = {
  common: {
    photo: 'Photo',
    continue: 'Continue',
    dragToReorder: 'Drag to reorder', dragToMove: 'Drag to move',
    select: "Select", noResults: "No results",
    save: 'Save', cancel: 'Cancel', edit: 'Edit', delete: 'Delete', add: 'Add',
    back: 'Back', close: 'Close', copy: 'Copy', copied: 'Copied!',
    loadingApp: 'Loading Gromly',
    loading: 'Loading…', search: 'Search', open: 'Open', confirm: 'Confirm',
    enable: 'Enable', disable: 'Disable', create: 'Create', invite: 'Invite',
    remove: 'Remove', generate: 'Generate',
    fromDocument: 'From document', fromDocumentHint: 'Imported from your document — your fixed base. The assistant builds around it and won\'t reorganize or remove it.',
    deleting: 'Deleting…', required: 'Required', confirmDelete: 'Are you sure? This cannot be undone.',
    clear: 'Clear', today: 'Today',
    pickTime: 'Pick a time', pickDate: 'Pick a date', pickDateTime: 'Pick date & time', time: 'Time', done: 'Done',
    areYouSure: 'Sure?', yesDelete: 'Yes, delete', deleteFailed: 'Could not delete',
    accept: 'Accept', discard: 'Discard', ai: 'AI', or: 'or',
    error: 'Something went wrong', saving: 'Saving…',
  },
  upload: {
    fileTooLarge: '“{{name}}” is too large — the limit is {{size}}',
    fileUnsupported: '“{{name}}” is not a supported file type',
    imageUnreadable: 'We couldn’t read the photo “{{name}}”. Try exporting it as JPEG.',
    storageFull: "You’ve reached your photo storage limit",
    storageFullBatch_one: "You’ve reached your photo storage limit — {{count}} photo wasn’t uploaded",
    storageFullBatch_other: "You’ve reached your photo storage limit — {{count}} photos weren’t uploaded",
    progress: { title: "Uploading photo {{current}} of {{total}}", keepOpen: "Keep this page open until it finishes" },
  },
  nav: {
    myTrips: 'My trips', menu: 'Menu', docs: 'Docs', signOut: 'Sign out', security: 'Security', agencyHome: "Home", collapseMenu: "Collapse menu", expandMenu: "Expand menu", agencyTrips: "Trips", mode: { label: "Switch to", personal: "My trips", admin: "Administration" }, explore: 'Explore', accountSettings: 'Account settings',
    lightMode: 'Switch to light mode', darkMode: 'Switch to dark mode', language: 'Language',
  },
  auth: {
    unverified: {
      title: 'Verify your email',
      body: 'We sent a link to {{email}}. Some actions (invite, publish) are blocked until you confirm it.',
      resend: 'Resend',
      resent: 'Email resent. Check your inbox.',
      alreadyVerified: 'Your email is already verified, reload the page.',
      resendFailed: 'Could not resend. Try again in a few minutes.',
    },
    login: {
      heroTitle: 'Plan every\nadventure',
      heroSubtitle: 'Itineraries, bookings, expenses and the little notes your crew will remember — kept in one quiet place, shared only with the people going.',
      heroFeatures: { flights: 'Flights', hotels: 'Hotels', places: 'Places', expenses: 'Expenses', checklists: 'Checklists', share: 'Share' },
      heroEyebrow: 'Where every trip begins',
      heroLine1: 'Plan the', heroLine2: 'unforgettable,', heroLine3: 'skip the', heroLine4: 'rest.',
      title: 'Welcome back', subtitle: 'Sign in to your account',
      email: 'Email address', password: 'Password', submit: 'Sign in',
      noAccount: 'No account?', createOne: 'Create one',
      errorDefault: 'Invalid email or password',
      googleAccount: 'This account signs in with Google. Pick it in the account chooser, or set a password for it.',
      appleAccount: 'This account signs in with Apple. Use Continue with Apple, or set a password for it.',
      setPassword: 'Set a password for this account',
      tooManyAttempts: 'Too many attempts. Wait a few minutes and try again.',
      hidePassword: 'Hide password', showPassword: 'Show password',
      breadcrumb: 'Account · sign in',
      newHere: 'New here?',
      createAccount: 'Create an account',
      continueJourney: 'Continue your journey.',
      continueSubtitle: 'Sign in to pick up where you left off — your trips are where you left them.',
      forgot: 'Forgot?',
      passwordPlaceholder: 'At least 8 characters',
      rememberMe: 'Keep me signed in on this device',
      orContinueWith: 'or continue with',
      continueWithGoogle: 'Continue with Google',
      continueWithApple: 'Continue with Apple',
      footer: { privacy: 'Privacy', terms: 'Terms', status: 'Status', copyright: '© 2026 Gromly · Vestigi Platform, S.L.' },
    },
    forgotPassword: {
      eyebrow: 'Account recovery',
      title: 'Forgot your password?',
      subtitle: "Type the email you signed up with and we'll send you a link to choose a new password.",
      emailLabel: 'Email address',
      submit: 'Send reset link',
      failed: 'Could not send the email. Try again in a few minutes.',
      sentTitle: 'Check your inbox',
      sentBody: "If {{email}} has an account with us, a reset link is on its way.",
      sentHint: "The link expires in an hour and only works once. Can't find it? Check your spam folder.",
      backToLogin: 'Back to sign in',
    },
    resetPassword: {
      eyebrow: 'New password',
      title: 'Choose a new password',
      subtitle: 'Pick something you can remember. Signing in elsewhere will need it too.',
      checking: 'Checking your link…',
      newPassword: 'New password',
      confirmPassword: 'Repeat password',
      confirmPlaceholder: 'Type it again',
      tooShort: 'At least {{min}} characters.',
      mismatch: "The two passwords don't match.",
      submit: 'Change password',
      failed: 'Could not change your password. Try again.',
      twoFactorNeeded: 'This account has two-factor authentication. Enter your code to continue.',
      twoFactorHint: 'From your authenticator app — the same code you use to sign in.',
      invalidTitle: 'This link no longer works',
      invalidBody: 'Reset links expire after an hour and can only be used once. Ask for a fresh one and it will arrive in seconds.',
      requestNew: 'Request a new link',
      doneTitle: 'Password changed',
      doneBody: "You're signed out everywhere else. Sign in with your new password to pick up where you left off.",
      goToLogin: 'Go to sign in',
    },
    twoFactor: {
      title: 'Two-factor authentication',
      subtitle: 'Enter the 6-digit code from your authenticator app',
      codeLabel: 'Authentication code',
      verify: 'Verify',
      back: 'Back to sign in',
      invalidCode: 'Invalid or expired code',
      modalTitle: 'Two-factor authentication',
      statusEnabled: '2FA is currently enabled. Your account is protected with an authenticator app.',
      statusDisabled: '2FA is currently disabled. Enable it to add an extra layer of security.',
      enable: 'Enable 2FA',
      disable: 'Disable 2FA',
      scanQr: 'Scan this QR code with Google Authenticator, Authy, or any TOTP app.',
      manualEntry: 'Or enter this secret manually:',
      confirm: 'Activate 2FA',
      confirmDisable: 'Disable 2FA',
      disablePrompt: 'Enter your current authentication code to disable 2FA.',
      setupError: 'Could not start 2FA setup. Try again.',
    },
    register: {
      nationality: "Nationality",
      heroTitle: 'Your next\nadventure awaits',
      heroSubtitle: 'Create an account and start building\nunforgettable trips with your people.',
      heroFeatures: ['Day-by-day itinerary builder', 'Shared expenses & debt tracking', 'Venues, bookings & maps', 'Invite travel partners'],
      heroEyebrow: 'Your next adventure starts here',
      heroLine1a: 'Every great ', heroLine1b: 'trip', heroLine2a: 'begins with a ', heroLine2b: 'plan.',
      heroSubheading: 'Build itineraries, split expenses and keep all your travel details in one quiet place — shared only with the people going.',
      title: 'Create account', subtitle: 'Start planning your trips',
      displayName: 'Display name', username: 'Nickname', email: 'Email', password: 'Password',
      passwordPlaceholder: 'At least 8 characters', submit: 'Create account',
      hasAccount: 'Already have an account?', signIn: 'Sign in',
      errorDefault: 'Registration failed',
      hidePassword: 'Hide password', showPassword: 'Show password',
      invitePreview: 'After creating your account we will join you to the trip "{{tripName}}" of {{inviterName}}.',
      mustAcceptTerms: 'You must accept the terms and the privacy policy to continue.',
      toastJoined: 'You joined the trip',
      toastInviteExpired: 'Account created, but we could not add you to the trip (invitation expired)',
      toastVerifyEmail: 'We sent you an email to verify your account',
      breadcrumb: 'Account · create',
      alreadyMember: 'Already a member?',
      signInLink: 'Sign in',
      tripInviteHeader: 'Trip invitation',
      newAccountHeader: 'New account',
      startAdventure: 'Start your adventure.',
      createSubtitle: 'Create your account and begin planning your first trip.',
      acceptStart: 'I have read and agree to the',
      terms: 'Terms of Service',
      and: 'and the',
      privacy: 'Privacy Policy',
      or: 'or',
      orWithEmail: 'or with email',
    },
    verify: {
      checking: 'Verifying email…',
      success: 'Email verified',
      failed: "We couldn't verify",
      successBody: 'Your account is active. You can now invite friends to your trips and publish content.',
      failedBody: 'The link is invalid or has expired. From your profile you can request a new link.',
      goToDashboard: 'Go to dashboard',
      goHome: 'Go home',
    },
    completeProfile: {
      eyebrow: 'One more step',
      title: 'Complete your profile.',
      subtitle: 'Pick a nickname and accept our policies to get started.',
      nicknameLabel: 'Nickname',
      nicknameHint: 'Minimum 3 characters. Letters, numbers and underscore.',
      acceptStart: 'I have read and accept the',
      terms: 'Terms of Service',
      and: 'and the',
      privacy: 'Privacy Policy',
      continue: 'Continue',
      mustAccept: 'You must accept the terms and the privacy policy to continue.',
      saveFailed: 'Could not save profile',
    },
    callback: {
      signingIn: 'Signing you in…',
    },
  },
  invite: {
    errors: {
      invalidTitle: 'Invitation not valid',
      invalidBody: 'The link does not match any active invitation.',
      cancelledTitle: 'Invitation cancelled',
      cancelledBody: 'The person who invited you has cancelled this invitation.',
      expiredTitle: 'Invitation expired',
      expiredBody: 'This link has expired. Ask for a new invitation.',
      usedTitle: 'Invitation already used',
      usedBody: 'This link has already been used.',
      ownTitle: 'You already own this trip',
      ownBody: "This link is meant for someone else. If you want to test how an invitation is accepted, open it in an incognito window or with another account.",
      emailMismatchTitle: "This invitation is for another email",
      emailMismatchBody: "Your travel agency sent it to a different address. Sign in with the email you gave them.",
      failedTitle: 'Something went wrong',
      failedBody: "We couldn't load the invitation. Please try again.",
    },
    joined: 'You joined the trip',
    goDashboard: 'Back to dashboard',
    goHome: 'Go home',
    eyebrow: 'Trip invitation',
    inviterText: '{{inviterName}} invites you',
    toCollaborate: 'To collaborate on the trip "{{tripName}}".',
    roleEditor: 'Editor',
    roleViewer: 'Read only',
    joining: 'Joining the trip…',
    needAccount: "To accept you need a Gromly account. If it's your first time, create one in 30 seconds. If you already have an account, sign in.",
    createAndJoin: 'Create account and join',
    haveAccount: 'I already have an account · sign in',
    expiresOn: 'Expires on {{date}}',
  },
  dashboard: {
    deleteTripAria: 'Delete trip',
    greeting: { morning: 'Good morning', afternoon: 'Good afternoon', evening: 'Good evening' },
    title: 'Your adventures', tagline: 'Plan together. Travel better.', searchPlaceholder: 'Search trips…',
    newTrip: 'New trip', newTripShort: 'New', addTripHint: 'Add destination, dates and travelers',
    stats: { totalTrips: 'Total trips', destinations: 'Destinations', upcoming: 'Upcoming', nights: 'Nights', avgBudget: 'Avg budget', subPast: '{{count}} past', subUnique: 'unique', subTotal: 'total', subPerTrip: 'per trip', subCredits: 'credits' },
    filter: { all: 'All', upcoming: 'Upcoming', past: 'Past' },
    sections: { following: 'Trips you follow', upcoming: 'Upcoming', past: 'Past', horizon: 'On the horizon', brewing: 'Brewing trips', alsoPlanning: 'Also planning', pastKicker: "Where you've been", pastTitle: 'Memory lane', memoryLane: 'Memory lane · already lived', seeAll: 'See all {{count}}' },
    eyebrow: { trips_one: '{{count}} trip', trips_other: '{{count}} trips', lived_one: '{{count}} lived', lived_other: '{{count}} lived' },
    summary: { label: 'Summary', trips_one: '{{count}} trip', trips_other: '{{count}} trips', dests_one: '{{count}} destination', dests_other: '{{count}} destinations', nights_one: '{{count}} night', nights_other: '{{count}} nights', avg: '{{amount}} avg', credits: '{{credits}} credits' },
    toast: {
      loadFailed: 'Failed to load trips',
      deleted: 'Trip deleted',
      published: 'Trip published to community!',
      unpublished: 'Trip unpublished',
    },
    welcome: {
      title: 'Welcome to Gromly!',
      body: "We've created a demo trip to Barcelona so you can explore all features. Delete it whenever you're ready.",
      explore: 'Explore demo →',
      create: 'Create my first trip',
    },
    community: {
      removeTitle: 'Remove from community',
      shareTitle: 'Share trip with community',
      removeBody: '{{name}} will be removed from the community and no longer visible to other travellers.',
      shareBody: '{{name}} will be published to the community feed and visible to all travellers on Gromly.',
      remove: 'Remove',
      publish: 'Publish trip',
      shared: 'Shared',
      share: 'Share',
      community: 'Community',
    },
    hero: {
      onRoadNow: 'On the road now',
      nextAdventure: 'Your next adventure',
      departingIn: 'Starts in',
      day: 'day',
      days: 'days',
      daysLeft: 'Days left',
      left: 'left',
      onRoad: 'On the road',
      planning: 'Planning',
      night: 'night',
      nights: 'nights',
      openItinerary: 'Open itinerary',
      fieldDestination: 'Destination',
      fieldDates: 'Dates',
      fieldTravelers: 'Travelers',
      people_one: '{{count}} person',
      people_other: '{{count}} people',
    },
    card: {
      active: 'Active',
      tomorrow: 'Tomorrow',
      inDays_one: 'in {{count}} day',
      inDays_other: 'in {{count}} days',
    },
    today: 'Today!', tomorrow: 'Tomorrow', daysUntil: '{{count}}d', pastLabel: 'Past',
    delete: 'Delete', open: 'Open',
    deleteTrip: { title: 'Delete this trip?', body: '"{{name}}" and all its itinerary, places, expenses and bookings will be permanently deleted.' },
    empty: {
      kicker: 'Start here',
      title: 'Plan your first trip',
      subtitle: 'Create a trip, invite your travel partner, and start organising your itinerary, places, expenses and checklists — all in one place.',
      cta: 'New trip',
      exploreCta: 'Explore trips',
      featuresLabel: 'Features',
      features: [
        { label: 'Day-by-day itinerary', desc: 'Activities, times and order' },
        { label: 'Places & map', desc: 'Save spots and see the route' },
        { label: 'AI planning', desc: 'Let AI draft your trip' },
        { label: 'Flights & hotels', desc: 'Bookings and tickets in one place' },
        { label: 'Shared expenses', desc: 'Automatic debt settlement' },
        { label: 'Plan as a team', desc: 'Invite others and plan together' },
        { label: 'Lists & packing', desc: 'Documents and packing checklists' },
        { label: 'Photo gallery', desc: 'Your memories, day by day' },
      ],
    },
    modalTitle: 'Plan a new trip',
    newTripWizard: {
      progress: "Step {{current}} of {{total}}",
      back: "Back",
      stepLabel: {
        destination: "Destination",
        dates: "Dates",
        legs: "Nights",
        travellers: "Travellers",
        bookings: "Bookings",
        start: "Getting started",
        documents: "Documents",
        style: "Your style",
        summary: "Summary",
      },
      ask: {
        destination: "Where are you going?",
        dates: "When?",
        legs: "How many nights in each place?",
        travellers: "Who's coming?",
        bookings: "Do you already have bookings?",
        start: "How do you want to start?",
        documents: "Upload what you already have",
        style: "How do you like to travel in {{destination}}?",
        summary: "Your trip is ready to go",
      },
      hint: {
        destination: "A city, a region or a whole country. Going to several? Add them one by one.",
        legs: "They have to add up to the nights of the trip.",
        travellers: "Add everyone travelling: you can share expenses, flights and the suitcase with them from day one.",
        bookings: "Flights, accommodation, transport or tickets, sorted by type. We'll read them and add them to Docs with the file attached. Each type uses one AI credit.",
        start: "You can change everything afterwards.",
        documents: "Bookings, tickets or an itinerary in PDF, Word or Excel. Nothing is added until you confirm.",
        style: "So the suggestions fit you.",
        summary: "Check it over and give it a name.",
      },
      travellersCount_one: "Just me",
      travellersCount_other: "{{count}} of us",
      travellersFewer: "One fewer",
      travellersMore: "One more",
      travellerDefaultName: "Traveller {{number}}",
      ticketsCount_one: "{{count}} booking",
      ticketsCount_other: "{{count}} bookings",
      ticketsNone: "None",
      ticketsSkip: "Skip",
      ticketsMax: "Up to {{count}} files per type.",
      ticketsAddTo: "Add files to {{kind}}",
      travellerName: "Name",
      travellerRemove: "Remove traveller",
      you: "you",
      birthDateLabel: "Date of birth (optional)",
      birthDateHint: "Optional. With dates of birth we can let you know about age-based fares and perks, and fit the plan better to who's coming.",
      emailLabel: "Email",
      emailPlaceholder: "name@email.com",
      emailHint: "We'll send them an invitation to join the trip.",
      addEmail: "Invite by email",
      start: {
        recommended: "Recommended",
        ai: {
          title: "Plan it with AI",
          description: "We suggest places, a day-by-day itinerary and practical tips.",
        },
        documents: {
          title: "I have documents",
          description: "Upload bookings or an itinerary and we build the trip from them.",
        },
        empty: {
          title: "Start from scratch",
          description: "Create the empty trip and fill it in at your own pace.",
        },
      },
      documentsFound: "This is what we found",
      complementAi: "Fill in the gaps with AI",
      complementAiHint: "We add places and an itinerary around what your documents already cover.",
      paceLabel: "Pace",
      budgetLabel: "Budget",
      interestsLabel: "What are you in the mood for?",
      notesLabel: "Anything else? (optional)",
      generatingTitle: "Preparing your trip",
      invitesFailed_one: "An invitation couldn't be sent — you can send it from Participants.",
      invitesFailed_other: "{{count}} invitations couldn't be sent — you can send them from Participants.",
      groupForPrompt_one: "one person travelling alone",
      groupForPrompt_other: "a group of {{count}} (the travellers list has their ages)",
      destinationPlaceholder: 'e.g. Lisbon, Portugal',
      destinationAddAnother: "Add another destination",
      destinationRemove: "Remove {{name}}",
      legNights_one: "{{count}} night",
      legNights_other: "{{count}} nights",
      legNightsFewer: "One fewer night in {{name}}",
      legNightsMore: "One more night in {{name}}",
      legsTotal_one: "{{assigned}} of {{count}} night",
      legsTotal_other: "{{assigned}} of {{count}} nights",
      legDays: "Days {{from}}–{{to}}",
      legDay: "Day {{day}}",
      legDaysShort: "days {{from}}–{{to}}",
      legForPrompt_one: "{{name}} ({{count}} night)",
      legForPrompt_other: "{{name}} ({{count}} nights)",
      datesPlaceholder: "e.g. Aug 3 to Aug 10, or 'the first week of September'",
      datesParseFailed: "I couldn't quite catch those dates — try rephrasing them?",
      datesPickHint: 'or pick them on the calendar below',
      datesAnswer_one: '{{start}} – {{end}} · {{count}} night',
      datesAnswer_other: '{{start}} – {{end}} · {{count}} nights',
      preferencesPlaceholder: 'e.g. relaxed pace, we love local food...',
      interestsLoading: 'Finding out what people usually do here…',
      interestsFailed: "Couldn't load suggestions for this destination — you can still continue.",
      pace: { relaxed: 'Relaxed', balanced: 'Balanced', packed: 'Packed' },
      budget: { budget: 'Budget-friendly', mid: 'Mid-range', splurge: 'Splurge' },
      nameLabel: 'Trip name',
      currencyLabel: 'Base currency',
      currencyHint: 'Balances and the budget are shown in this currency; expenses in other currencies are converted to it.',
      defaultName: 'Trip to {{destination}}',
      next: 'Continue',
      editAnswer: 'Edit answer',
      importFromDocument: 'Import from documents',
      documentsRemove: "Remove documents",
      importing: 'Reading your document…',
      importFailed: "We couldn't read that document — try another file or continue manually.",
      importInsufficientCredits: "You're out of AI credits — you can still create the trip manually.",
      create: 'Create trip', created: 'Trip created!', failed: 'Failed to create trip',
      noSpecificInterests: 'no particular interests specified',
      placesSeedPrompt: "We're planning a {{pace}} {{nights}}-night trip to {{destination}} for {{travelers}}, {{budget}} budget. We're especially interested in: {{interests}}. {{notes}} Suggest around {{targetCount}} great places to visit that fit this — a mix of the interests above plus a couple of solid restaurant or cafe options nearby.",
      itinerarySeedPrompt: 'Organize all the places you just saved into a full day-by-day itinerary covering the whole trip, keeping a {{pace}} pace.',
      tipsSeedPrompt: 'Give me the most essential and practical tips for traveling to {{destination}} — entry requirements (visa, ETA, ESTA…), recommended vaccinations or health precautions, travel insurance, and any other genuinely useful local tips.',
      generating: {
        stepPlaces: 'Places in {{destination}}',
        stepPlacesFound_one: '{{count}} found',
        stepPlacesFound_other: '{{count}} found',
        stepItinerary_one: 'Your {{count}}-night itinerary',
        stepItinerary_other: 'Your {{count}}-night itinerary',
        stepItineraryDone_one: '{{count}} day',
        stepItineraryDone_other: '{{count}} days',
        stepTips: 'Practical tips for {{destination}}',
        stepTipsDone_one: '{{count}} tip',
        stepTipsDone_other: '{{count}} tips',
        stepTickets: "Your bookings",
        stepTicketsDone_one: "{{count}} added",
        stepTicketsDone_other: "{{count}} added",
        ticketsUnplaced_one: "We couldn't read {{count}} document — it's saved in Notes so you can add it from Docs.",
        ticketsUnplaced_other: "We couldn't read {{count}} documents — they're saved in Notes so you can add them from Docs.",
        dayStops_one: 'Day {{number}} · {{count}} stop',
        dayStops_other: 'Day {{number}} · {{count}} stops',
        doneEmpty: 'Trip created — you can add places anytime from the Places tab.',
        errorCredits: "Out of AI credits — your trip was created, but we couldn't generate suggestions this time.",
        errorUnknown: 'Something went wrong generating suggestions — your trip was still created.',
        cta: 'View my trip',
      },
    },
  },
  birthDate: {
    day: "Day",
    month: "Month",
    year: "Year",
  },
  import: {
    sidebarButton: 'Import from document',
    title: 'Import from documents',
    description: 'Upload a Word, PDF, Excel or text file with your plan and we\'ll pull out the places and day-by-day itinerary. Nothing is added until you confirm.',
    dropHint: 'Click to choose files',
    addMore: 'Add more files',
    analyze_one: 'Analyze {{count}} document',
    analyze_other: 'Analyze {{count}} documents',
    formats: 'PDF, Word, Excel, CSV or images · up to 10 files',
    extracting: 'Reading your documents…',
    docProgress: "Document {{current}} of {{total}}",
    reviewHint: 'Pick what to add to your trip. Existing items are never changed.',
    savedDocsNote: 'Imported documents',
    language: {
      title: 'This document is in {{document}}',
      hint: 'Your trip is in {{trip}}. Translate what you import so the whole trip speaks one language. Place names stay as they are.',
      action: 'Translate to {{trip}}',
      done: 'Translated into the trip\'s language',
      failed: 'Couldn\'t translate. You can still add it as it is.',
    },
    placesSection_one: '{{count}} place',
    placesSection_other: '{{count}} places',
    daySection: 'Day {{number}}',
    daySectionNoNumber: 'Itinerary',
    activitiesSection_one: "{{count}} activity",
    activitiesSection_other: "{{count}} activities",
    packingCount_one: "{{count}} packing item",
    packingCount_other: "{{count}} packing items",
    flightsSection_one: '{{count}} flight',
    flightsSection_other: '{{count}} flights',
    hotelsSection_one: '{{count}} stay',
    hotelsSection_other: '{{count}} stays',
    nightsCount_one: '{{count}} night',
    nightsCount_other: '{{count}} nights',
    bookingsSection_one: '{{count}} booking',
    bookingsSection_other: '{{count}} bookings',
    tipsSection_one: '{{count}} tip',
    tipsSection_other: '{{count}} tips',
    packingSection: 'Packing · {{section}}',
    expensesSection_one: '{{count}} expense',
    expensesSection_other: '{{count}} expenses',
    addSelected_one: 'Add {{count}} item',
    addSelected_other: 'Add {{count}} items',
    added_one: 'Added {{count}} item',
    added_other: 'Added {{count}} items',
    mapsQuotaWarning: 'Some places couldn\'t be located on the map this time, but they were still imported.',
    errors: {
      nothingFound: "We couldn't find any places or itinerary in that document.",
      extractFailed: "We couldn't read that document — try another file.",
      insufficientCredits: "You're out of AI credits.",
      rateLimited: "We're getting a lot of requests right now — wait a few seconds and try again.",
      saveFailed: 'Something went wrong adding the items — please try again.',
    },
  },
  trip: {
    editName: 'Click to rename the trip',
    renameFailed: 'Could not rename the trip',
    destinations: {
      title: "Destinations", edit: "Change destinations",
      hint: "The places the trip visits, in order, and how many nights in each.",
      saved: "Destinations updated", failed: "Could not change the destinations",
      moveUp: "Move {{name}} up", moveDown: "Move {{name}} down",
    },
    dates: {
      title: 'Trip dates', edit: 'Change dates', confirm: 'Apply',
      saved: 'Dates updated', failed: 'Could not change the dates',
      willMove_one: "The trip gets shorter. 1 stop from day {{from}} moves to day {{to}}.",
      willMove_other: "The trip gets shorter. {{count}} stops from day {{from}} move to day {{to}}.",
      willLoseMeals_one: "1 meal planned on the days that disappear will be lost — meals go with their day.",
      willLoseMeals_other: "{{count}} meals planned on the days that disappear will be lost — meals go with their day.",
      willLoseOutfits_one: "Clothes planned for 1 day that disappears will be lost — they stay in the suitcase.",
      willLoseOutfits_other: "Clothes planned for {{count}} days that disappear will be lost — they stay in the suitcase.",
      willAdd_one: 'One empty day is added at the end.', willAdd_other: '{{count}} empty days are added at the end.',
      noLoss_one: 'The trip keeps its 1 day, on the new date.', noLoss_other: 'All {{count}} days keep their stops, on the new dates.',
      bookingsOutside_one: '1 booking falls outside the new dates', bookingsOutside_other: '{{count}} bookings fall outside the new dates',
      bookingsKept: "They keep their own dates — a booking reference is a fact, so nothing is moved for you.",
      aFlight: 'Flight',
    },
    nav: {
      itinerary: 'Itinerary', places: 'Places', map: 'Map',
      bookings: 'Bookings', expenses: 'Expenses', meals: 'Meals', packing: 'Packing', info: 'Tips',
      plan: 'Plan', placesShort: 'Places', mapShort: 'Map',
      docsShort: 'Docs', money: 'Money', mealsShort: 'Meals', packingShort: 'Packing', infoShort: 'Tips',
      notes: 'Notes', notesShort: 'Notes',
      services: 'Services', servicesShort: 'Services',
      gallery: 'Diary', galleryShort: 'Diary', album: 'Album',
      more: 'More',
    },
    sidebar: {
      allTrips: 'All trips', sections: 'Sections', documentation: 'Documentation', collapse: 'Collapse sidebar', expand: 'Expand sidebar',
      nights_one: '{{count}} night', nights_other: '{{count}} nights',
      flights_one: '{{count}} flight', flights_other: '{{count}} flights',
      people: '{{count}} people',
      manage: 'Manage',
      workspace: 'Workspace',
      travellers_one: 'traveller',
      travellers_other: 'travellers',
      underway: 'Underway',
      tomorrow: 'Tomorrow',
      inDays: 'In {{count}} days',
      ready: '{{pct}}% ready',
      exportPdf: 'Export PDF',
      generating: 'Generating…',
      exportFailed: 'Failed to generate PDF',
    },
    cover: {"title": "Cover photo", "hint": "Shown on the trip's card and header. Framed 3:2.", "add": "Upload a photo", "remove": "Use the automatic cover", "cropTitle": "Frame the cover", "save": "Save cover", "saved": "Cover updated", "removed": "Back to the automatic cover", "failed": "Could not update the cover", "unsupported": "That file isn't a photo we can use", "alt": "Trip cover", "changeAction": "Change cover"},
    contentLanguage: {
      title: 'Trip language',
      hint: 'The language the assistant writes trip content in — places, itinerary, tips and packing. Only affects new content; what is already saved stays as it is.',
      saved: 'Trip language updated',
      failed: 'Could not update the trip language',
    },
    manage: { title: 'Manage trip' },
    participants: {
      roles: { OWNER: 'Owner', EDITOR: 'Editor', VIEWER: 'Viewer' },
      title: 'Trip participants',
      you: '(you)',
      pending: 'Pending invitations',
      invite: 'Invite someone by email',
      sendInvitation: 'Invite',
      hint: "They'll receive an email with a link to join. Both roles count as travellers: they share expenses and have their own packing list.",
      viewerHint: 'Only the trip owner can invite or remove travelers.',
      roleEditor: 'Editor · travels and edits',
      roleViewer: 'Read only · travels, does not edit',
      remove: 'Remove',
      cancelInvite: 'Cancel',
      roleLabel: 'Role: {{role}} · Expires',
      age_one: "{{count}} year old",
      age_other: "{{count}} years old",
      placeholder: {
        addBirthDate: "Add date of birth",
        editBirthDate: "Change date of birth",
        noAccount: 'No account',
        addTitle: 'Add someone without an account',
        addHint: "For whoever hasn't signed up yet: you can put expenses, flights and packing items in their name, and link them to their account later.",
        namePlaceholder: 'Name',
        add: 'Add',
        link: 'Link to an account',
        inviteHint: 'Send them an invitation. When they accept, everything in their name moves to their account.',
        mergeHint: 'Already in the trip with their own account?',
        mergeSelect: 'Choose who they are',
        merge: 'Merge',
        confirmMergeTitle: 'Merge {{name}} into {{target}}?',
        confirmMergeBody: "Everything assigned to {{name}} — expenses, debts, flights and packing items — moves to {{target}}. This can't be undone.",
        invitationFor: 'for {{name}}',
        toast: {
          birthDateFailed: "Could not save the date of birth",
          added: '{{name}} added to the trip',
          addFailed: 'Could not add them',
          merged: 'Merged',
          mergeFailed: 'Could not merge',
        },
      },
      toast: {
        invited: 'Invitation sent',
        emailNotVerified: 'Verify your email before inviting other users',
        inviteFailed: 'Could not send invitation',
        revoked: 'Invitation cancelled',
        revokeFailed: 'Could not cancel',
        removed: 'Participant removed',
        roleChanged: 'Role updated',
        roleChangeFailed: 'Could not update role',
      },
    },
    notifications: {
      reminder_one: '{{count}} upcoming reminder', reminder_other: '{{count}} upcoming reminders',
      title: 'Reminders',
    },
    notFound: 'Trip not found',
    demo: {
      banner: 'This is a sample trip — feel free to explore everything.',
      deleteConfirm: 'Delete this demo trip?',
      deleteFailed: 'Failed to delete demo trip',
    },
    tour: {
      start: 'Take the tour',
      skip: 'Skip',
      back: 'Back',
      next: 'Next',
      finish: 'Got it',
      steps: {
        importDoc: {
          title: "Already have your trip written down?",
          body: "Upload a Word, PDF or Excel — even flight or hotel tickets — and we'll pull out the places, itinerary, bookings and expenses for you.",
        },
        welcome: {
          title: 'Welcome to Gromly 👋',
          body: 'This is a sample trip to Barcelona. Take a quick tour to see everything you can do — you can skip it any time.',
        },
        itinerary: {
          title: 'Plan your itinerary',
          body: 'Build your trip day by day: add activities, set times and drag to reorder.',
        },
        itineraryAi: {
          title: 'Meet gIA — your assistant, everywhere',
          body: 'gIA now travels with you across every screen, docked here on the right. It adapts to wherever you are — itinerary, places, bookings, packing or tips — so just expand it to ask for ideas, build a whole day or reshuffle things. Your conversation follows you between tabs.',
        },
        places: {
          title: 'Save places to visit',
          body: 'Collect restaurants, monuments and neighbourhoods by category — and the AI can suggest new places that fit your trip.',
        },
        map: {
          title: 'See it all on the map',
          body: 'Your saved places and each day’s activities, all on one map.',
        },
        mapRoutes: {
          title: 'Routes on the map',
          body: 'Switch to “Routes” to see every selected day at once — travel times, transport per leg, and “Optimize with gIA” to reorder a day’s stops.',
        },
        bookings: {
          title: 'Keep your bookings',
          body: 'Store flights, hotels and tickets in one place. Paste a confirmation and the AI extracts the details for you.',
        },
        expenses: {
          title: 'Track and split costs',
          body: 'Log what you spend, set a budget and split expenses with fellow travellers.',
        },
        balances: {
          title: 'Who owes whom',
          body: 'Gromly nets out every shared expense and tells you exactly who owes whom — no maths needed.',
        },
        packing: {
          title: 'Packing',
          body: 'Packing lists and documents so you don’t forget a thing — the AI can even build the list from your destination and dates.',
        },
        tips: {
          title: 'Tips',
          body: 'Handy local tips on transport, food and safety — the AI can generate ones tailored to your destination.',
        },
        notes: {
          title: 'Notes',
          body: 'A shared notepad for ideas, links and anything you want to remember.',
        },
        gallery: {
          title: 'Gallery',
          body: 'Upload your trip photos and browse them organised day by day.',
        },
        finish: {
          title: 'You’re all set!',
          body: 'That’s the tour. Explore freely — this demo trip is yours to play with, and you can delete it whenever you’re ready to plan for real.',
        },
      },
    },
    share: {
      disable: 'Disable sharing', enable: 'Enable sharing', creating: 'Creating…',
      disabledToast: 'Sharing disabled', enabledToast: 'Share link created!',
      panelTitle: 'Show the trip to someone who is not on it',
      panelHint: 'A link that opens the trip without an account. Whoever opens it is not a traveller: they do not count in the per-person split, have no suitcase and are not in the head count. Pick what it shows.',
      sectionsTitle: 'What the link shows',
      sectionsHint: 'This link was created before these options existed, so it still shows what it always did. Tick what you want to add.',
      photosWarning: 'Photo links keep working once copied, even if you switch this back off.',
      diaryHint: 'Each author decides which entries go out. Followers can sign up by email from the link. Turning the link off removes them all.',
      section: {
        ITINERARY: 'Itinerary',
        NOTES: 'Notes on stops and places',
        PLACES: 'Saved places',
        MAP: 'Map',
        MEALS: 'Meals',
        TIPS: 'Tips',
        PHOTOS: 'Photos',
        DIARY: 'Diary',
        BOOKINGS: 'Flights, stays and bookings',
      },
    },
    story: {
      entry: 'Instagram story',
      title: 'Instagram story',
      upload: 'Upload a photo',
      background: 'Background',
      gradient: { paper: 'Cream', ink: 'Black' },
      framingHint: "Drag to move the photo, scroll to zoom",
      statDays: 'Days', statStops: 'Stops', statDistance: 'Distance',
      download: 'Download', share: 'Share',
      hint: 'Save the image and add it to your story — 1080 × 1920.',
      previewAlt: 'Story preview',
      imageFailed: 'Could not load that image', exportFailed: 'Could not create the image',
    },
  },
  linkedDelete: { both: "Delete both" },
  itinerary: {
    stopPhotos_one: '{{count}} photo of this stop', stopPhotos_other: '{{count}} photos of this stop',
    // ADR-0039: a stop of the travellers' own, as agency staff see it.
    maskedStop: {
      title: "The travellers' plan",
      hint: "It's theirs: the agency doesn't see what it is.",
    },
    deleteLinked: {
      title_one: "This stop is a planned meal",
      title_other: "{{count}} of these stops are planned meals",
      body_one: "Delete the meal from Meals too?",
      body_other: "Delete those meals from Meals too?",
      only_one: "Only the stop",
      only_other: "Only the stops",
    },
    suggestTime: {
      use: 'use',
      hint: 'Worked out from the previous stop and the travel between them. Click to use it.',
      noTravel: 'Worked out from the previous stop. Travel time is not counted yet.',
    },
    schedule: {
      from: 'from',
      dayStart: 'Day starts at',
      setDayStart: 'Set a start time',
      clear: 'Back to manual times',
      hint: 'Times are worked out from this hour, each stop’s length and the travel between them.',
      manual: 'Times on this day are the ones you typed.',
      estimated: 'estimated',
      noTravel: 'Travel time not counted yet — calculate it and the day will shift.',
      overrun: 'Does not fit before this fixed time',
      locked: 'Fixed time',
      timeMode: 'Time mode',
      modeAuto: 'Calculated',
      modeFixed: 'Fixed',
      autoHint: 'Adjusts itself to the order, durations and travel.',
      fixedHint: 'Stays put even if you reorder the day.',
    },
    title: 'Itinerary',
    subtitle_one: '{{count}} day · {{nights}} nights',
    subtitle_other: '{{count}} days · {{nights}} nights',
    views: { list: 'List', timeline: 'Timeline' },
    addDay: 'Add day',
    heading: 'The plan',
    daysPlanned_one: '{{count}} day planned',
    daysPlanned_other: '{{count}} days planned',
    legend: { hasActivities: 'Has activities', today: 'Today' },
    dayLabel: 'Day {{n}}',
    dayShort: 'D{{n}}',
    weather: {
      condition: { clear: 'Clear', fair: 'Mostly clear', partlyCloudy: 'Partly cloudy', cloudy: 'Cloudy', fog: 'Fog', drizzle: 'Light rain', rain: 'Rain', snow: 'Snow', thunder: 'Thunderstorms' },
      typicalFor: 'typical for {{month}}',
      aria: '{{condition}}, high {{max}}°, low {{min}}°',
      ariaTypical: 'Typical for {{month}}: high {{max}}°, low {{min}}°',
      at: 'Measured at {{place}}',
      atTrip: 'Measured at {{place}} — the trip destination, not a stop of this day',
      tripDestination: 'trip destination',
      panel: {
        title: 'Hour by hour',
        subtitle: 'How the weather moves through each day of the trip',
        open: 'See it hour by hour',
        noHourly: 'The hourly forecast only reaches about 9 days ahead.',
        noData: 'No weather for this day.',
        blocksLegend: 'More than ~3 days out the forecast gives four readings a day, shown as they come. The full hourly curve appears as those days get closer.',
        beyondForecast: 'Beyond the forecast',
        precip: '{{mm}} mm',
        wind: '{{kmh}} km/h',
      },
    },
    modal: { addActivity: 'Add activity', editActivity: 'Edit activity', addToItinerary: 'Add to itinerary', addToDays: 'Add to {{count}} days', dayLabel: 'Day', addedToDay: 'Added to Day {{n}}', addedToDays: 'Added to {{count}} days', removedFromItinerary: 'Removed from itinerary', itinerarySaved: 'Itinerary updated', unassignHint: 'Deselect a day to remove it from the itinerary.', noDaysYet: 'No itinerary days yet.', noDaysDesc: 'Add days in the Itinerary tab first.' },
    ai: { open: 'Open AI assistant', close: 'Close AI assistant' },
    activities_one: '{{count}} activity',
    activities_other: '{{count}} activities',
    flight: { outbound: 'Outbound', return: 'Return' },
    fromBooking: 'Booking',
    fromBookingHint: 'Comes from a booking in the Bookings tab — edit the booking and every night updates with it.',
    hotel: { checkIn: 'Check-in', checkOut: 'Check-out', night: 'Night' },
    continuesFrom: 'Continues from',
    startFromPrev: { active: 'Start here', inactive: 'Set as start', disable: 'Stop continuing from previous day', lastOfPrevDay: 'Last stop of the previous day' },
    optional: { badge: 'Optional', label: 'Optional stop', hint: 'Skip it without guilt if the day runs late.', mark: 'Mark as optional', unmark: 'Mark as must-do' },
    openInMaps: 'Open in Google Maps',
    openBooking: 'Open booking link',
    delete: { title: 'Delete activity?', ariaLabel: 'Delete activity', body: 'This action cannot be undone.', bodyLocked: 'This activity is the starting point for the next day — deleting it will also remove it from there.' },
    bulk: { selected_one: '{{count}} selected', selected_other: '{{count}} selected', delete: 'Delete selected', cancel: 'Cancel', select: 'Select', move: 'Move to…', moveTo: 'Move to day', duplicate: 'Duplicate to…', duplicateTo: 'Duplicate on day', deleting: 'Deleting…' },
    stopCount_one: '{{count}} stop', stopCount_other: '{{count}} stops',
    legs: { calculate_one: 'Travel time ({{count}} leg)', calculate_other: 'Travel times ({{count}} legs)' },
    move: {
      dropOnDay: 'Drop on a day to move it there',
      releaseOnDay: 'Release to move it to day {{day}} · {{date}}',
      confirmTitle_one: 'Move “{{title}}” to day {{day}}?', confirmTitle_other: 'Move {{count}} stops to day {{day}}?',
      confirm: 'Move',
      moved_one: 'Moved to day {{day}}', moved_other: '{{count}} stops moved to day {{day}}',
      duplicated_one: 'Copy created', duplicated_other: '{{count}} copies created',
      failed: 'Could not move it', duplicateFailed: 'Could not duplicate it',
      errorOrigin: "The “continues from” card follows the previous day — it can’t be moved on its own.",
      errorStayNight: "A night of a stay has no date of its own — change the dates of the booking instead.",
      errorBookingCard: "A card generated by a booking can’t be duplicated — duplicate the booking instead.",
    },
    form: {
      titleField: 'Title', titlePlaceholder: 'e.g. Lunch at Alfama',
      yourTypes: 'Your types', newType: '+ New type…', newTypeName: 'Name of the type', newTypePlaceholder: 'e.g. Snorkel, Wine tasting',
      type: 'Type',
      startTime: 'Start time', duration: 'Duration', durationHours: 'h', durationMinutes: 'min',
      venueName: 'Venue / Place name', zone: 'Zone / Neighborhood',
      bookingUrl: 'Booking URL', notes: 'Notes',
      notesPlaceholder: 'Cash only, book in advance...',
      update: 'Update', addActivity: 'Add activity', saveAndAddAnother: 'Save and add another',
      day: 'Day',
      dayBookingHint: 'Comes from a booking — changing the day moves the booking too.',
      dayStayHint: 'One card per night slept — change the stay dates in Bookings.',
      fromPlaces: 'From saved places', filterPlaces: 'Filter places…', noMatchPlaces: 'No places match',
    },
    types: {
      RESTAURANT: 'Restaurant', CAFE: 'Café', MONUMENT: 'Monument', VIEWPOINT: 'Viewpoint',
      NEIGHBORHOOD: 'Neighborhood', PARK: 'Park', SHOP: 'Shop', EXPERIENCE: 'Experience',
      TRANSPORT: 'Transport', PARKING: 'Parking', BEACH: 'Beach', NATURE: 'Nature', ACCOMMODATION: 'Accommodation', OTHER: 'Other',
    },
    toast: {
      added: 'Activity added!', addedWithPlace: 'Activity added · {{name}} saved to Places',
      updated: 'Activity updated',
      deleted: 'Activity deleted', failed: 'Failed to save activity',
      loadFailed: 'Failed to load itinerary', removedSuccess: 'Activity removed',
      movedToDay: 'Moved to day {{n}}',
      optimizeFailed: 'Could not optimise with AI', optimizeNoResult: 'Could not optimise the route',
      optimizeApplied: 'Optimised route applied', reorderFailed: 'Could not save the new order',
    },
    undo: { action: 'Undo', undoLabel: 'Undo (Ctrl+Z)', redoLabel: 'Redo (Ctrl+Shift+Z)', undone: 'Undone', failed: 'Could not undo — the plan has been reloaded', reordered: 'Order changed', legMode: 'Travel mode changed' },
    empty: {
      noDays: 'No days yet',
      noDaysDesc: 'Days are generated automatically from your trip dates. To add more days, extend the end date in the trip settings.',
      clickDay: 'Click a trip day to see activities',
      noEvents: 'No events to show. Add days and activities first.',
      noActivities: 'No activities yet',
      addStop: 'Add a stop', planWithAi: 'Plan it with gIA', fromSaved: 'From my saved places',
      planPrompt: "Plan day {{n}} for me: pick the stops, put them in a sensible order and give each one a time.",
    },
    dow: { mon: 'Mon', tue: 'Tue', wed: 'Wed', thu: 'Thu', fri: 'Fri', sat: 'Sat', sun: 'Sun' },
    dayUnit_one: 'DAY', dayUnit_other: 'DAYS',
  },
  venues: {
    title: 'Places to visit',
    categories: {
      RESTAURANT: 'Restaurants', CAFE: 'Cafés', ATTRACTION: 'Attractions',
      VIEWPOINT: 'Viewpoints', NEIGHBORHOOD: 'Neighborhoods', PARK: 'Parks',
      SHOP: 'Shops', EXPERIENCE: 'Experiences', TRANSPORT_HUB: 'Transport',
      BEACH: 'Beaches', NATURE: 'Nature', PARKING: 'Parkings', OTHER: 'Other',
    },
    filter: { all: 'All', toVisit: 'To visit', visited: 'Visited', day: 'Day', scheduled: 'On a day', unscheduled: 'No day' },
    noDaySection: 'Not on any day',
    progress: '{{visited}} of {{total}} visited',
    placesCount_one: 'PLACE', placesCount_other: 'PLACES',
    addPlace: 'Add place', editPlace: 'Edit place', addToItinerary: 'Add to itinerary',
    addToDay: 'Add to day',
    mapView: 'Map', maps: 'Maps', book: 'Book',
    markVisited: 'Mark visited', markUnvisited: 'Unmark visited', visited: 'Visited',
    viewOnMaps: 'View on Google Maps',
    openNow: 'Open now', closedNow: 'Closed now', fullSchedule: 'View full schedule', closedDay: 'Closed',
    hoursDisclaimer: 'Opening hours may not be 100% up to date — check with the venue.',
    showDetails: 'See photos & reviews', showDetailsSource: '· via Google',
    detailsUnavailable: 'No extra info available for this place.',
    deleted: 'Place deleted',
    form: {
      name: 'Name', category: 'Category', address: 'Address',
      priceInfo: 'Price info', bookingUrl: 'Booking URL', notes: 'Notes',
      searchPlace: 'Search a place or address…',
      namePlaceholder: "e.g. Joe's Pizza", pricePlaceholder: 'e.g. ~20€/person', notesPlaceholder: 'Cash only, book 2 weeks ahead…',
      customCategory: 'Kind of place', customCategoryPlaceholder: 'e.g. Cave, Winery, Hot spring',
      yourCategories: 'Your categories', newCategory: '+ New category…',
    },
    bulk: { addToDay: 'Add to day…', scheduled_one: 'Added to day {{n}}', scheduled_other: '{{count}} places added to day {{n}}' },
    noMatch: 'No places match this filter.',
    day_one: 'Day {{days}}', day_other: 'Days {{days}}',
    empty: {
      title: 'Build your places list',
      body: 'Save every restaurant, monument, café and viewpoint. Mark them as visited as you explore, and link directly to Google Maps for navigation.',
      addFirst: 'Add first place',
      askAi: 'Ask AI for suggestions',
    },
    delete: {
      title: 'Delete place',
      body: 'Are you sure you want to delete this place? This action cannot be undone.',
      confirm: 'Delete',
      withStops_one: 'It is also a stop on day {{days}} — that stop goes too.',
      withStops_other: 'It is also a stop on days {{days}} — those stops go too.',
    },
    toast: {
      loadFailed: 'Failed to load places',
      deleted: 'Place removed',
      added: '{{name}} added to your places',
      addFailed: 'Failed to add the place',
      updated: 'Place updated',
      saveFailed: 'Failed to save place',
      suggestionAdded_one: '{{count}} new place added',
      suggestionAdded_other: '{{count}} new places added',
      suggestionFailed: 'Could not generate more suggestions',
      suggestionError: 'Error generating suggestions',
    },
  },
  map: {
    routeError: 'Could not calculate route', stopsCount_one: '{{count}} stop', stopsCount_other: '{{count}} stops',
    travelMode: { WALKING: 'Walk', DRIVING: 'Drive', BICYCLING: 'Bike', TRANSIT: 'Transit', FERRY: 'Ferry', FLIGHT: 'Flight', TRANSFER: 'Transfer', ferryLeg: '{{duration}} by ferry', unavailable: 'Not available for this leg', fallbackToast_one: 'That travel mode has no route here — switched to one that does', fallbackToast_other: 'No route in that travel mode on {{count}} legs — switched to ones that do' },
    allPlaces: 'All places',
    dayLabel: 'Day {{n}}',
    showOnlyCategory: 'Show only {{label}}',
    destinationChip: { showWholeTrip: 'Show the whole trip', showOnly: 'Show only {{name}}' },
    removeCategoryFilter: 'Remove {{label}} filter',
    showAllCategories: 'Show all categories',
    collapsePanel: 'Collapse panel', expandPanel: 'Expand panel',
    quota: { title: 'Monthly map limit reached', body: 'Places, routes and searches are paused until the limit resets.', bodyWithDate: 'Places, routes and searches are paused until the limit resets on {{date}}.', hint: 'Everything else in your trip keeps working normally.' },
    unavailable: { title: 'The map could not load', body: 'Google Maps didn’t respond. Check your connection and try again in a moment.' },
    dayFilter: 'Day',
    showAll: 'Show all', hideAll: 'Hide all',
    showAllDays: "Show every day",
    optionalStops: 'Optional',
    optionalStopsHint: 'Hide the optional stops. The drawn route still runs through them — the faded stretch is what skipping them would save.',
    dayNotCalculated: "No route could be calculated for this day",
    scrollLeft: 'Scroll left', scrollRight: 'Scroll right',
    searchPlaces: 'Search places…',
    legFromTo: 'Travel from {{from}} to {{to}}',
    calculating: 'Calculating…',
    routes: 'Routes',
    daysCount_one: '{{count}} day', daysCount_other: '{{count}} days',
    noDaysSelected: 'No days selected',
    savedPlaces: 'Saved places', savedPlacesHint: 'Show saved places that aren’t on any route yet',
    showOnMap: 'Show {{name}} on the map',
    places: 'Places', fullRoute: 'Full route',
    calculatingRoutes: 'Calculating routes…',
    optimizing: 'Optimizing…',
    optimizeWith: 'Optimize with',
    optimize: 'Optimize',
    openInMaps: 'Open in Maps',
    recenter: 'Recenter map',
    savePlaceModal: 'Save place',
    addToItineraryModal: 'Add to itinerary',
    aiOptimizePrompt: 'Optimise the order of today’s stops to minimise travel.',
    aiOptimizeHintPrefix: 'User directions (they take priority over the generic criteria)',
    aiHint: {
      title: 'AI directions',
      subtitle: 'Optional. Specify times, meals or specific preferences.',
      placeholder: 'E.g. Sagrada Família in the morning, tapas dinner around 9pm…',
    },
    center: 'Center', save: 'Save',
    aiProposalTitle: 'AI Route Suggestion', proposedOrder: 'Proposed order',
    filter: 'Filter',
    locatingPlaces: 'Locating your places…', locationsToFind: 'locations to find',
    noDaysYet: 'No itinerary days yet', addDaysFirst: 'Add days in the Itinerary tab first.',
    loadError: 'Failed to load Google Maps. Check your API key.',
    collapseStops: 'Collapse stops', expandStops: 'Expand stops', tapToSeeStops: 'Tap to see stops',
  },
  bookings: {
    expenses: { paid: 'Paid for this booking', add: 'Add expense' },
    types: { TRANSPORT: 'Transport', EVENT: 'Event', ACTIVITY: 'Activity', RESTAURANT: 'Restaurant', OTHER: 'Other' },
    title: 'Bookings & Tickets', subtitle: 'Flights, hotels, transport and event tickets',
    sections: { flights: 'Flights', hotels: 'Hotels & Accommodation', transport: 'Transport', events: 'Events & Tickets' },
    form: {
      direction: 'Direction', outbound: '→ Outbound', return: '← Return',
      from: 'From (IATA)', to: 'To (IATA)', departure: 'Departure', arrival: 'Arrival',
      flightNumber: 'Flight number', airline: 'Airline',
      bookingRef: 'Booking reference', notes: 'Notes',
      hotelName: 'Hotel name', address: 'Address', checkIn: 'Check-in', checkOut: 'Check-out',
      currency: 'Currency', bookingUrl: 'Booking URL',
      category: 'Category', title: 'Title', provider: 'Provider / Company',
      date: 'Date', startTime: 'Start time', endTime: 'End time',
      startDate: 'Start date', endDate: 'End date',
      endDateHint: 'Only if it spans several days',
      endDateBeforeStart: 'End date is before the start date',
      venue: 'Venue / Place', price: 'Price', confirmed: 'Confirmed',
      documents: 'Documents (PDFs, images, emails)',
      saveChanges: 'Save changes', addFlight: 'Add flight', addHotel: 'Add hotel', addBooking: 'Add booking',
      legs: 'Legs', addLeg: 'Add leg', passengers: 'Passengers',
      searchPlace: 'Search a place or address…',
      extractWithAi: 'Fill in with AI', extracting: 'Reading document…', addWithAi: 'Add with AI',
      addWithAiTitle: 'Add with AI',
      addWithAiExplanation: "Upload a ticket, boarding pass or booking confirmation (a PDF, a photo, or the confirmation email saved as .eml) and we'll read it for you, attachments included: dates, times, flight numbers, booking references and more get filled in automatically. You'll still get to review everything before saving.",
      addWithAiDropTitle: 'Drop your file here', addWithAiDropSubtitle: 'or click to browse — PDF, image or email (.eml)',
      unreviewedTitle_one: '1 more flight to review', unreviewedTitle_other: '{{count}} more flights to review',
      unreviewedBody_one: 'It was read from your document but isn\'t saved yet. If you close now, it won\'t be added.',
      unreviewedBody_other: 'They were read from your document but aren\'t saved yet. If you close now, they won\'t be added.',
      unreviewedDiscard: 'Discard', unreviewedKeep: 'Keep reviewing',
      stepIndicator: 'Step {{current}} of {{total}}', saveAndContinue: 'Save & continue',
      legNumber: 'Leg {{n}}', removeLeg: 'Remove leg', arrivalBeforeDeparture: 'arrival ≤ departure',
      hotelNamePlaceholder: 'The Grand Hotel',
      bookingTitlePlaceholder: 'e.g. Renfe AVE Madrid–Barcelona',
      venuePlaceholder: 'Bernabéu, Teatro Real…',
    },
    reminder: {
      startsIn: 'Starts in {{delay}}', endsIn: 'Ends in {{delay}}',
      endTitle: 'End · {{title}}',
      label: 'Reminder before event', minutes: 'minutes', hours: 'hours',
      warnNoTime: 'Set a start time for the reminder to trigger',
    },
    docs: {
      title: 'Documents', upload: 'Upload file',
      dropHere: 'Drop a PDF, image or email (.eml) here, or click to upload',
      attachFile: 'Attach ticket, PDF, screenshot or email', addMore: 'Add more files',
      deleteTitle: 'Delete document?', deleteConfirm: 'Delete "{{name}}"? This cannot be undone.',
    },
    deleteItem: {
      flightTitle: 'Delete flight?', hotelTitle: 'Delete hotel?', bookingTitle: 'Delete booking?',
    },
    empty: {
      noFlights: 'No flights added yet', noHotels: 'No hotels added yet',
      noTransport: 'Add trains, buses, ferries or car rentals',
      noEvents: 'Add concerts, theatre, sports, tours, restaurant reservations…',
      addOne: 'Add one',
    },
    notifications: {
      blocked: 'Notifications are blocked. Enable them in browser settings to receive booking reminders.',
      prompt: 'Enable browser notifications to receive reminders before your bookings.',
      enableBtn: 'Enable',
    },
    status: { outbound: 'Outbound', return: 'Return', confirmed: 'Confirmed', pending: 'Pending' },
    modal: {
      editFlight: 'Edit flight', addFlight: 'Add flight',
      editHotel: 'Edit hotel', addHotel: 'Add hotel',
      editBooking: 'Edit booking', addBooking: 'Add booking',
    },
    toast: {
      loadFailed: 'Failed to load bookings', saveFailed: 'Error saving',
      flightDeleted: 'Flight deleted', hotelDeleted: 'Hotel deleted', bookingDeleted: 'Booking deleted',
      flightAdded: 'Flight added', flightUpdated: 'Flight updated',
      hotelAdded: 'Hotel added', hotelUpdated: 'Hotel updated',
      bookingAdded: 'Booking added', bookingUpdated: 'Booking updated',
      fileDeleted: 'File deleted', uploadFailed: 'Upload failed',
      uploadFileFailed: 'Failed to upload {{name}}',
      filesUploaded_one: '{{count}} file uploaded', filesUploaded_other: '{{count}} files uploaded',
      extractSuccess: 'Details filled in — review before saving', extractFailed: 'Could not read the document',
      extractNoMatch: 'Could not match passenger(s): {{names}}. Add them manually.',
    },
    yourTickets: 'Your tickets',
    bookingsCount_one: 'BOOKING', bookingsCount_other: 'BOOKINGS',
    durationDays_one: '{{count}} day', durationDays_other: '{{count}} days',
    view: { sections: 'Sections', timeline: 'Timeline' },
    timeline: { dayLabel: 'Day {{n}}', noTickets: 'No tickets', ongoingDay: 'day {{n}}/{{total}}', ongoingEnd: 'last day' },
    ticket: {
      from: 'From', to: 'To', direct: 'Direct',
      layover_one: '{{count}} stop', layover_other: '{{count}} stops',
      layoverAt: 'Stopover at {{airport}}',
    },
  },
  expenses: {
    reservationGroups: { FLIGHT: 'Flights', HOTEL: 'Accommodation', BOOKING: 'Other bookings' },
    travellersCount_one: '{{count}} TRAVELLER', travellersCount_other: '{{count}} TRAVELLERS',
    title: 'Expenses',
    ledger: 'The ledger',
    redistribute: {
      title: 'New members on the trip',
      desc_one: '{{names}} joined after some expenses were logged. Split the {{count}} earlier shared expense with them too?',
      desc_other: '{{names}} joined after some expenses were logged. Split the {{count}} earlier shared expenses with them too?',
      apply: 'Split with them',
      dismiss: 'Not now',
      done_one: 'Redistributed {{count}} expense',
      done_other: 'Redistributed {{count}} expenses',
      failed: 'Could not redistribute the expenses',
    },
    stats: { totalSpent: 'Total spent', expenses: 'Expenses', biggest: 'Biggest' },
    subtitle_one: '{{count}} entry', subtitle_other: '{{count}} entries',
    addExpense: 'Add expense', editExpense: 'Edit expense',
    delete: { link: 'Delete expense', title: 'Delete this expense?' },
    expenseCount_one: '{{count}} expense', expenseCount_other: '{{count}} expenses',
    splits_one: '{{count}} split', splits_other: '{{count}} splits',
    chart: { totalSpent: 'Total spent', total: 'total' },
    groupBy: { all: 'All', byCategory: 'By category' },
    scan: {
      button: 'Scan a receipt',
      aiButton: 'Scan with AI',
      scanning: 'Reading…',
      success: 'Receipt read — check the details',
      failed: "Couldn't read the upload. Fill it in manually.",
      title: 'Scan expenses with AI',
      explanation: 'Upload a receipt, or a screenshot of your bank or wallet app listing several transactions — every expense found comes back here for you to review.',
      dropTitle: 'Drop a receipt or a screenshot here',
      dropSubtitle: 'Images or PDF · up to {{count}} files',
      reviewHint_one: '{{count}} expense found. Untick what you don\'t want, and tap a row to edit it.',
      reviewHint_other: '{{count}} expenses found. Untick what you don\'t want, and tap a row to edit it.',
      noDescription: 'No description',
      noRate: 'no rate',
      rateFailed: "Couldn't get today's rate for {{currencies}}.",
      fixRow: 'Complete the highlighted expense first.',
      include: 'Include this expense',
      exclude: 'Leave this expense out',
      selectedTotal_one: '{{count}} selected',
      selectedTotal_other: '{{count}} selected',
      addSelected_one: 'Add {{count}} expense',
      addSelected_other: 'Add {{count}} expenses',
      savedCount_one: '{{count}} expense added',
      savedCount_other: '{{count}} expenses added',
      savedFailed_one: "{{count}} expense couldn't be saved",
      savedFailed_other: "{{count}} expenses couldn't be saved",
    },
    form: {
      reservation: 'Linked booking', reservationNone: '— None —',
      description: 'Description', descriptionPlaceholder: 'e.g. Hotel booking',
      category: 'Category', payment: 'Payment', amount: 'Amount',
      currency: 'Currency', date: 'Date', status: 'Status',
      splitBetween: 'Split between', perPerson: '{{currency}} {{amount}} / person',
      notes: 'Notes', saveChanges: 'Save changes', addExpense: 'Add expense',
      paidBy: 'Paid by', you: 'You', noCurrencies: 'No results',
      conversion: {
        title: 'Convert to {{currency}}',
        loading: 'Loading…', refresh: 'Refresh rate',
        manual: 'Rate unavailable — enter it manually.',
        asOf: 'Rate as of {{date}}',
      },
    },
    categories: {
      ACCOMMODATION: 'Accommodation', FLIGHTS: 'Flights', FOOD: 'Food',
      ACTIVITIES: 'Activities', TRANSPORT: 'Transport',
      FUEL: 'Fuel', PARKING: 'Parking', CASH: 'Cash', OTHER: 'Other',
    },
    payment: { CARD: 'Card', CASH: 'Cash', TRANSFER: 'Transfer' },
    status: { PAID: 'Paid', PENDING: 'Pending' },
    settlement: {
      title: 'Settlement plan', even: 'Everyone is even',
      allDone: 'All transfers confirmed', pending: '{{pending}} of {{total}} pending',
      allEven: 'Everyone is even — no transfers needed!',
      confirmed: 'Confirmed', settle: 'Settle', undo: 'Undo',
      onlySelf: 'Add more participants to the trip to split expenses and see who owes whom.',
      payIn: 'Pay in', markSettled: 'Mark settled',
      rateUnavailable: 'Exchange rate unavailable',
      paidAs: 'Paid as {{amount}} {{currency}}',
    },
    budget: {
      title: 'Budgets', manage: 'Budgets', set: 'Set budgets', edit: 'Edit',
      empty: 'No budgets set',
      emptyDesc: 'Set spending caps by total, day, category or person and get warned before you overspend.',
      total: 'Total trip', daily: 'Per day',
      categorySection: 'By category', personSection: 'By person',
      tripSection: 'Whole-trip limits',
      tripSectionHint: 'These caps apply to the entire trip, not per day.',
      dailySection: 'Daily allowance',
      dailyHint: 'A cap for each single day — we’ll warn you when a day gets close or goes over.',
      noLimit: 'No limit',
      leftAmount: '{{amount}} left', overAmount: 'Over by {{amount}}', atLimit: 'Limit reached',
      worstDay: 'Worst day: {{amount}}',
      daysOver_one: '{{count}} day over the cap', daysOver_other: '{{count}} days over the cap',
      save: 'Save budgets', saved: 'Budgets saved', failed: 'Couldn’t save budgets',
      alertsTitle: 'Budget alerts',
      overToast: 'Heads up — you just went over your {{label}} budget',
      warnToast: 'Getting close to your {{label}} budget',
    },
    debts: {
      title: 'Settled debts', recalculate: 'Recalculate',
      showSettled: 'Show settled ({{count}})', hideSettled: 'Hide settled ({{count}})',
    },
    empty: {
      title: 'Track shared expenses',
      desc: 'Log every expense and split it between travellers. The app calculates who owes what and lets you settle debts with one tap.',
      addFirst: 'Add first expense',
    },
    toast: {
      settled: 'Debt settled!', recalculated: 'Debts recalculated', deleted: 'Expense removed',
      updated: 'Expense updated!', added: 'Expense added!',
      failedUpdate: 'Failed to update expense', failedAdd: 'Failed to add expense',
      selectParticipant: 'Select at least one participant', loadFailed: 'Failed to load expenses',
      rateRequired: 'Enter a valid exchange rate',
    },
    filters: {
      button: 'Filter', clear: 'Clear', day: 'Day', category: 'Category',
      paidBy: 'Paid by', remove: 'Remove {{filter}}',
      showing_one: '{{count}} expense', showing_other: '{{count}} expenses',
    },
    table: {
      all: 'ALL', noFilter: 'No expenses match this filter',
      date: 'Date', item: 'Item', category: 'Category', paidBy: 'Paid by', split: 'Split', amount: 'Amount',
      editRow: 'Edit {{description}}',
      linkedReservation: 'Linked to a booking',
    },
    spendingByPerson: 'Spending by person',
    byPerson: {
      traveller: 'Traveller', paid: 'Paid', share: 'Their share', balance: 'Balance',
      formerTraveller: 'Former traveller',
      hint: 'Paid is what they put in. Their share is what the split says it costs them.',
    },
    spendingByDay: 'Spending by day',
    byCategory: 'By category',
    whoOwesWhom: 'Who owes whom',
    settledCount: 'Settled · {{count}}',
    baseCurrency: {
      title: 'Base currency',
      desc: 'Balances and the budget are shown in this currency; expenses in other currencies are converted to it. You can only change it before adding any expense.',
      edit: 'Change base currency',
      saving: 'Saving…',
      saved: 'Base currency updated',
      failed: 'Could not update base currency',
    },
  },
  info: {
    deleteTip: 'Delete tip',
    tipsSectionTitle: 'Tips',
    title: 'Travel Tips', subtitle: 'Destination-based tips for your trip',
    tips: 'Travel tips',
    heading: 'Travel tips',
    quickSuggestions: 'Quick suggestions',
    acceptAndAdd: 'Accept & add',
    discard: 'Discard',
    saving: 'Saving…',
    tipsProposalLabel: 'Proposal · {{count}} tips',
    tipsAdded_one: '{{count}} tip added',
    tipsAdded_other: '{{count}} tips added',
    loadFailed: 'Failed to load tips',
    tipsGenerateFailed: 'Failed to generate tips',
    tipsSaveFailed: 'Failed to save tips',
    tipsAiDefault: 'Give me the most useful tips for this destination.',
    tipsAiPlaceholder: 'e.g. tips for a first-time visitor…',
    tipsAiMorePlaceholder: 'Ask for more tips or adjustments…',
    addTipCategoryPlaceholder: 'Category',
    addTipContentPlaceholder: 'Tip content…',
    addTipBtn: 'Add tip',
    addTipLabel: 'Add a tip',
    suggestedCategories: 'Suggested tip categories',
    tipCategories: ['Transport', 'Visa & Entry', 'Money & ATMs', 'Safety', 'Language', 'Local customs', 'SIM card', 'Weather'] as string[],
    tipsPrompts: ['Essential tips for this destination', 'Public transport and taxis', 'Money, tipping and ATMs', 'Safety and common scams', 'Useful phrases in local language', 'Local customs'] as string[],
  },
  meals: {
    savedFlash: "Saved",
    title: 'Meals',
    subtitle: 'What you eat each day, and what you need to buy for it.',
    slot: {
      BREAKFAST: 'Breakfast', LUNCH: 'Lunch', SNACK: 'Snack',
      DINNER: 'Dinner', OTHER: 'Other',
    },
    addTo: 'Add {{slot}}',
    addAtPlace: 'At a restaurant',
    pickSaved: 'Pick a saved one',
    placeholder: 'Pasta with salmon, dinner at Dill…',
    notesPlaceholder: 'Notes (optional)',
    linkVenue: 'Link a place',
    editMeal: 'Edit meal',
    noVenue: 'No place',
    searchPlace: 'Search a restaurant or café…',
    venueHint: 'Pick a restaurant or café you saved, or search one: it is saved to Places.',
    emptyDay: 'Nothing planned',
    ingredients: {
      title: 'Ingredients', add: 'Add ingredient',
      namePlaceholder: 'Salmon, pasta…', amountPlaceholder: 'Qty', amountLabel: 'Amount',
      unit: 'Unit', remove: 'Remove {{name}}',
      unitMismatch: 'This product is already counted in another unit',
      editAmount: 'Change amount of {{name}}',
    },
    units: { u: 'pcs', g: 'g', kg: 'kg', ml: 'ml', l: 'l' },
    pantry: {
      title: 'Pantry',
      hint: 'What you bought, minus what the meals of past days ate.',
      left: '{{amount}} left', missing: '{{amount}} short',
      planned: 'plan {{amount}}', bought: 'bought {{amount}}', pending: '{{amount}} on the list',
      addMissingFor: 'Add the missing {{name}} to the list',
      added: '{{name}} added to the list',
      addShort: 'Add', onList: 'On the list',
    },
    grocery: {
      setAmount: 'amount',
      amountLabel: 'Amount to buy', unitLabel: 'Unit to buy',
      title: 'Shopping list',
      add: 'Add to the list',
      placeholder: 'Fresh salmon',
      forMeal: 'for {{title}}',
      empty: 'Nothing to buy yet.',
      emptyHint: 'Add what the planned meals need, and tick it off in the shop.',
      bought_one: '{{count}} in the basket',
      bought_other: '{{count}} in the basket',
      clearBought: 'Clear what is bought',
      cleared_one: '{{count}} item removed',
      cleared_other: '{{count}} items removed',
      collapse: 'Hide the shopping list',
      expand: 'Show the shopping list',
    },
    toast: {
      created: 'Meal added',
      deleted: 'Meal removed',
      saveFailed: 'Could not save the meal',
      deleteFailed: 'Could not remove the meal',
      groceryFailed: 'Could not save the shopping list',
      stopFailed: "Could not update the stop in the itinerary",
    },
    // ADR-0024: a meal linked to a stop of its day.
    stop: {
      pick: "Link to a stop…",
      create: "Add to itinerary",
      timePlaceholder: "Time",
      hint: "Linked to a stop, the title and the place stay the same in both.",
      linked: "In the itinerary",
      linkedAt: "In the itinerary at {{time}}",
      otherDay: "Its stop is on day {{day}}",
      unlink: "Unlink",
      badge: "Plan",
    },
    deleteLinked: {
      title: "This meal is a stop in the itinerary",
      body: "Delete the stop too?",
      only: "Only the meal",
    },
  },
  packing: {
    outfits: {
      heading: "Clothes per day",
      hint: "Plan what you'll wear each day with the clothes in your suitcase. Split a day into moments only if it helps (beach, dinner…).",
      piecePlaceholder: "Type or pick a garment…",
      addNew: "Add “{{text}}” to your suitcase",
      addNewTo: "Add “{{text}}” to {{section}}",
      sectionEmpty: "Nothing left in this section — type a garment to add it.",
      fromSection: "From section",
      suitcaseEmpty: "No clothes in your suitcase yet — type a garment to add it.",
      addPiece: "Add clothes",
      addGroup: "Split by moments of the day",
      groupPrompt: "For which moment?",
      groupPresets: ["Beach", "Dinner", "Sport", "Hike", "Night out"],
      dragHint: "Drag it to another moment",
      allDay: "All day",
      groupPlaceholder: "Another moment…",
      groupName: "Moment name",
      renameGroup: "Rename moment",
      deleteGroup: "Remove moment",
      deleteGroupHint: "Its clothes go back to “All day”",
      removePiece: "Remove {{text}} from this day",
      packed: "In the suitcase",
      notPacked: "Not packed yet",
      pieceCount_one: "{{count}} item",
      pieceCount_other: "{{count}} items",
      emptyDay: "Nothing planned",
      loadFailed: "Could not load the clothes per day",
      addFailed: "Could not add the garment",
    },
    addSuggested_one: 'Add {{count}} section',
    addSuggested_other: 'Add {{count}} sections',
    addSectionFailed: 'Could not add the section',
    scope: {
      mine: 'Mine', common: 'Shared', someone: 'Someone',
    },
    sharing: {
      on: 'Visible to the group', off: 'Only me',
      onHint: 'The others can see your packing list. Click to keep it to yourself.',
      offHint: 'Only you can see your packing list. Click to share it with the group.',
      nowShared: 'Your packing list is now visible to the group',
      nowPrivate: 'Your packing list is now private',
      failed: 'Could not change who sees your packing list',
    },
    // Reusing a past trip's list: same-titled sections are merged, so the seven
    // empty defaults every trip starts with don't end up duplicated.
    copy: {
      button: 'Copy from another trip',
      title: 'Copy packing sections',
      pickTrip: 'Which trip do you want to copy from?',
      noTrips: 'You have no other trips to copy from yet.',
      backToTrips: 'Choose another trip',
      pickSections: 'Sections to copy',
      items_one: '{{count}} item',
      items_other: '{{count}} items',
      selectAll: 'Select all',
      clearAll: 'Clear',
      nothingToCopy: 'That trip has no packing sections with items yet.',
      mergeHint: 'Sections with the same name are merged, and items you already have are skipped.',
      confirm_one: 'Copy {{count}} section',
      confirm_other: 'Copy {{count}} sections',
      copying: 'Copying…',
      done_one: '{{count}} item added',
      done_other: '{{count}} items added',
      nothingNew: 'Those items were already on your list',
      loadFailed: 'Could not load that trip’s packing list',
      failed: 'Could not copy the sections',
    },
    assignee: { label: 'Who brings it', nobody: 'Nobody yet' },
    readOnlyHint: 'Someone else’s packing list — you can see it, but not change it.',
    emptySection: 'Nothing in here yet',
    completedProgress: '{{done}}/{{total}} completed · {{pct}}%',
    title: 'Packing', subtitle: 'Your pre-trip checklist',
    checklists: 'Pre-trip checklists',
    heading: 'Packing',
    quickSuggestions: 'Quick suggestions',
    acceptAndAdd: 'Accept & add',
    discard: 'Discard',
    saving: 'Saving…',
    packingProposalLabel: 'Proposal · {{sections}} sections · {{items}} items',
    sectionsAdded_one: '{{count}} section added to your checklist',
    sectionsAdded_other: '{{count}} sections added to your checklist',
    sectionRemoved: 'Section removed',
    loadFailed: 'Failed to load your packing list',
    packingGenerateFailed: 'Failed to generate packing list',
    packingGenerateSaveFailed: 'Failed to save packing list',
    packingAiDefault: 'Generate a complete personalised packing list.',
    packingAiPlaceholder: 'e.g. For a woman, 5-day summer trip…',
    packingAiMorePlaceholder: 'Ask for more sections or adjustments…',
    suggestedSections: 'Suggested sections',
    newSectionPlaceholder: 'New section (e.g. Documents, Reservations…)',
    addSection: 'Section',
    addSectionLabel: 'Add a section',
    addItemPlaceholder: 'Add item…',
    addItemBtn: 'Add',
    done: 'Done ✓',
    packingPrompts: ['For men', 'For women', 'Family with kids', 'Business trip', 'Backpacker', 'Beach / summer', 'Mountains / hiking'] as string[],
    defaultSectionNames: ['Documents', 'Clothing', 'Toiletries', 'Electronics', 'Medications', 'Reservations', 'Money'] as string[],
    sectionAiButton: 'Generate items with AI',
    sectionAiPlaceholder: 'Optional: how many days, climate, planned activities…',
    sectionAiGenerate: 'Generate',
    sectionAiPrompt: 'Generate ONLY new items to add to the packing checklist section "{{title}}" for this trip. Extra context from the user: {{info}}. Do not repeat items already in the list: {{existing}}. Return a single section named exactly "{{title}}" containing just the new items.',
    sectionAiNoExtraInfo: 'none provided',
    sectionAiNoExistingItems: 'none yet',
    sectionAiAdded_one: 'Added {{count}} item to "{{title}}"',
    sectionAiAdded_other: 'Added {{count}} items to "{{title}}"',
    sectionAiEmpty: "AI didn't suggest any new items for this section",
    sectionAiFailed: 'Failed to generate items — try again',
    dragHandle: 'Drag to reorder',
    reorderFailed: 'Could not save the new order',
    deleteSection: 'Delete section',
    deleteItem: 'Delete item',
    editItem: 'Click to edit',
    renameItemFailed: 'Could not rename the item',
    checkItem: 'Mark as packed',
    uncheckItem: 'Mark as not packed',
  },
  shared: {
    viewOnly: 'Shared trip (view only)',
    noDiary: 'Nothing written in the diary yet.',
    follow: { title: 'Follow this trip', hintAccount: "We'll let you know in the app when the group writes in the diary.", hintEmail: 'Get an email when the group writes in the diary.', follow: 'Follow', following: 'Following', followed: "You're following this trip", unfollowed: 'You no longer follow this trip', error: 'Something went wrong. Try again.', emailPlaceholder: 'Your email', subscribe: 'Notify me', checkInbox: 'Check your inbox to confirm.', confirmed: "Done! We'll email you when there's something new.", unsubscribed: "You won't get any more emails about this trip.", linkExpired: 'This link no longer works.' },
    notFoundTitle: 'Trip not found',
    notFoundDesc: 'This share link is invalid or the trip owner has disabled sharing.',
    nights_one: '{{count}} night', nights_other: '{{count}} nights',
    traveller_one: '{{count}} traveller', traveller_other: '{{count}} travellers',
    sections: {
      diary: 'Diary',
      flights: 'Flights', accommodation: 'Accommodation', itinerary: 'Itinerary', about: 'About this trip',
      places: 'Places', map: 'Map', travel: 'Travel',
      tips: 'Tips', photos: 'Photos', meals: 'Meals', bookings: 'Bookings',
    },
    footer: 'Shared with Gromly · View only',
    outbound: 'Outbound', return: 'Return', noActivities: 'No activities planned',
    optional: 'Optional',
    noPlaces: 'No places saved.', noTips: 'No tips yet.', noPhotos: 'No photos yet.', noBookings: 'Nothing booked yet.',
    openInMaps: 'Open in Google Maps',
    map: {
      allPlaces: 'All places', dayByDay: 'Day by day', dayLabel: 'Day {{n}}',
      unavailable: 'Map unavailable', noRouteYet: 'The route for this day has not been calculated yet.',
    },
  },
  account: {
    storage: { title: "Storage", photos: "Photos", used: "{{used}} of {{quota}}", full: "Your photo storage is full. Delete some photos to upload new ones." },
    avatar: { change: "Change photo", add: "Add photo", remove: "Remove photo", cropTitle: "Frame your photo", zoom: "Zoom", save: "Save photo", saved: "Profile photo updated", removed: "Profile photo removed", failed: "Could not update your photo", unsupported: "That file isn't a photo we can use" },
    birthDate: "Date of birth", birthDateHint: "Only you see it. Your trips only see your age, so we can flag age-based fares and perks.", birthDateSaved: "Date of birth saved", birthDateSaveFailed: "Could not save your date of birth",
    nationality: "Nationality", nationalitySaveFailed: "Could not update nationality",
    title: 'Account',
    subtitle: 'Manage your profile and preferences',
    profile: 'Profile',
    memberSince: 'Member since',
    credits: 'Credits',
    tripCredits: 'Trip credits',
    aiCredits: 'AI credits',
    plan: { title: "Plan", gift: "Gift from Gromly", free: "Free", proUntil: "PRO until {{date}}", manage: "Manage subscription", inStore: "Manage it in {{store}}", upgrade: "Get PRO", restore: "Restore purchases", restored: "Purchases restored", nothing: "No purchases to restore" },
    security: 'Security',
    changePassword: {
      title: 'Change password',
      rowHint: 'Update the password you sign in with',
      setTitle: 'Set a password',
      setHint: 'Your account has no password yet',
      setLinkSent: 'We sent you a link to set a password.',
      setLinkFailed: 'Could not send the link. Try again in a few minutes.',
      current: 'Current password',
      new: 'New password',
      confirm: 'Repeat new password',
      signsOutOthers: "You'll stay signed in here, but every other device will be signed out.",
      submit: 'Change password',
      success: 'Password changed.',
      wrongCurrent: 'That is not your current password.',
      unchanged: 'The new password is the same as the current one.',
      googleAccount: 'This account signs in with Google. Use the emailed link to set a password.',
      failed: 'Could not change your password. Try again.',
    },
    twoFactor: 'Two-factor authentication',
    enabled: 'Enabled',
    notEnabled: 'Not enabled',
    data: 'Data & Privacy',
    exportData: 'Export my data',
    exportDataDesc: 'Download a copy of all your data',
    danger: 'Danger zone',
    signOut: 'Sign out',
    deleteAccount: 'Delete account',
    deleteAccountDesc: 'Permanently delete your account and all data',
    deleteConfirmTitle: 'Delete account',
    deleteConfirmSubtitle: 'This action is permanent',
    deleteConfirmBody: 'All your trips, expenses, bookings, and personal data will be permanently deleted. This cannot be undone.',
    deleting: 'Deleting…',
    deletePermanently: 'Delete permanently',
    saveFirst: 'Want to save your data first?',
    exportFailed: 'Failed to export data. Please try again.',
    deleteFailed: 'Failed to delete account. Please try again.',
  },
  docs: {
    hero: {
      title: 'Gromly Docs',
      subtitle: 'Everything you need to know about planning, collaborating and travelling with Gromly.',
      back: 'Back to dashboard',
    },
    sidebar: { sections: 'Sections' },
    footer: { title: 'Ready to start planning?', subtitle: 'Go back to your dashboard and create your first trip.', cta: 'Go to dashboard' },
    itinerary: {
      title: 'Itinerary', subtitle: 'Plan your days, hour by hour',
      p1: 'The itinerary is the heart of your trip. Add activities to each day, reorder them by dragging, and each new day automatically continues from where the previous one ended.',
      feat_addDay_label: 'Add day', feat_addDay_desc: 'Click "Add day" to create a new day. Dates are assigned automatically from the trip start date.',
      feat_drag_label: 'Drag & drop', feat_drag_desc: 'Reorder activities within a day by dragging the grip handle.',
      feat_ai_label: 'AI planning', feat_ai_desc: 'Click the AI button to open the chat. Ask it to add activities, suggest what to visit, or move things around.',
      tip1: 'Switch between List and Timeline views using the toggle in the header. The Timeline view merges flights, hotels and activities into a single chronological feed.',
      step1: 'Click "Add day" to create your first day.',
      step2: 'Click "+ Add activity" to add activities to the day.',
      step3: 'Fill in the title, type (Restaurant, Monument, etc.) and optional details.',
      step4: 'Drag activities to reorder them within the day.',
    },
    places: {
      title: 'Places', subtitle: 'Your personal wishlist for the destination',
      p1: 'Places is your curated wishlist of spots to visit — restaurants you want to try, monuments to see, neighbourhoods to explore, shops to browse. Everything is organised by category.',
      feat_add_label: 'Add place', feat_add_desc: 'Use the "+ Add place" button or ask the AI to add it for you with automatic Google Maps enrichment.',
      feat_visited_label: 'Mark visited', feat_visited_desc: "Tap the checkmark on a place card once you've been there. Visited places are greyed out but kept in your list.",
      feat_ai_label: 'AI recommendations', feat_ai_desc: 'Open the AI chat and ask "Give me 5 restaurant recommendations in the old town". It will add them directly to your list.',
      tip1: 'Places added here automatically appear as pin options on the Map tab. You can link a place directly to an itinerary activity using the "Add to itinerary" button.',
    },
    map: {
      title: 'Map', subtitle: 'Visualise routes and optimise your day',
      p1: "The Map tab shows all your saved places as pins. Select a day from the dropdown to see only that day's activities with a drawn route between them.",
      feat_route_label: 'Day route', feat_route_desc: 'Select a day to see a connected route between all activities in order. Tap a pin to see the activity details.',
      feat_reorder_label: 'Reorder stops', feat_reorder_desc: 'In the route panel, drag the stops to change their order. The route on the map updates in real time.',
      feat_ai_label: 'AI optimise', feat_ai_desc: 'Tap "Optimise with gIA" to let the AI reorder your stops geographically to minimise backtracking.',
      tip1: 'On mobile, the route panel slides up from the bottom as a sheet. Swipe it down to hide it and get a full-screen map view.',
    },
    bookings: {
      title: 'Bookings', subtitle: 'Flights, hotels, transport and tickets — all in one place',
      p1: 'Bookings centralises every reservation for your trip: outbound and return flights, hotel check-ins, transport legs (train, bus, taxi), and event or activity tickets.',
      feat_flights_label: 'Flights', feat_flights_desc: 'Add outbound and return flights with airline, flight number, departure/arrival airports and booking reference.',
      feat_hotels_label: 'Hotels', feat_hotels_desc: 'Log your accommodation with check-in and check-out dates, address and booking URL.',
      feat_bookings_label: 'Bookings & tickets', feat_bookings_desc: 'Add any other booking: theatre, football match, museum entry, bus ticket — with date, venue and booking reference.',
      feat_docs_label: 'Attach documents', feat_docs_desc: 'Upload PDFs, screenshots, photos or the confirmation email itself (.eml) directly to a booking. Stored securely in the cloud.',
      tip1: 'Save the confirmation email as .eml and upload it as it is — the AI reads the message and its attached tickets, and it stays accessible offline. The app will show an in-app reminder 48 hours before each upcoming flight or hotel check-in.',
      warning1: 'Document uploads require Cloudflare R2 to be configured in the server environment. Contact your administrator if the upload button is disabled.',
    },
    expenses: {
      title: 'Expenses & Budget', subtitle: 'Track spending and settle debts automatically',
      p1: 'The Expenses tab is a full group expense tracker. Log what was spent, who paid, how it is split among the group, and let the app calculate the simplest way to settle up.',
      feat_add_label: 'Add expense', feat_add_desc: 'Record who paid, the amount, currency, category (Accommodation, Food, Transport…) and how to split it.',
      feat_settle_label: 'Auto settlement', feat_settle_desc: 'Click "Recalculate" to get a minimal set of payments that settles all debts. Each debt shows who owes who and how much.',
      feat_debt_label: 'Settle debt', feat_debt_desc: 'Once a debt is paid in real life, tap "Settle" to mark it as done. Settled debts are kept for reference.',
      feat_budget_label: 'Budget tracker', feat_budget_desc: "Tap the pencil icon on the Budget card to set a total budget. A progress bar shows how much you've used per category.",
      tip1: "The budget bar turns amber when you reach 80% of your budget and red when you go over. Set it at the start of the trip so you always know where you stand.",
      step1: 'Open Expenses and click "Add expense".', step2: 'Select who paid and the amount.',
      step3: 'Choose how to split: equally among all participants, or custom amounts.',
      step4: 'After adding several expenses, click "Recalculate" to get the settlement plan.',
      step5: 'When a payment is made, tap "Settle" to close the debt.',
    },
    info: {
      title: 'Info & Tips', subtitle: 'Tips, checklists and local knowledge',
      p1: 'Info & Tips has two sections: a free-form tips board organised by category (Transport, Food, Culture, Money…) and pre-trip checklists for packing and preparation.',
      feat_tips_label: 'Tips', feat_tips_desc: 'Add short tips by category. Great for notes like "Metro closes at midnight" or "Book restaurants 2 weeks ahead".',
      feat_checklists_label: 'Checklists', feat_checklists_desc: 'Create checklist sections (Documents, Clothing, Electronics…) and add items to each. Check them off as you pack.',
      feat_ai_label: 'AI packing list', feat_ai_desc: 'Click "Generate with AI" to get a personalised packing list based on your destination, duration, season and planned activities.',
      tip1: "The AI packing list is generated as checklist sections so you can edit, add or remove items afterwards. It's a starting point, not a fixed list.",
      step1: 'Add a checklist section (e.g. "Documents") with the text input at the bottom.',
      step2: 'Type items one by one and press Enter to add them.',
      step3: 'Or click "Generate with AI" to auto-create a full packing list.',
      step4: 'As you pack, check off items. The progress bar at the top tracks completion.',
    },
    ai: {
      title: 'AI Assistant', subtitle: 'Your smart travel companion',
      p1: "The AI assistant is context-aware — it knows your destination, travel dates and what's already in your trip. It works differently depending on which tab you're in.",
      feat_places_label: 'Places mode', feat_places_desc: 'Add "Sagrada Família to my places" and it will find it on Google Maps, fill in the address and create the venue automatically.',
      feat_itinerary_label: 'Itinerary mode', feat_itinerary_desc: 'Ask it to add activities to specific days and time slots, or to move existing activities. It sees your current itinerary.',
      feat_route_label: 'Route mode', feat_route_desc: 'In the Map tab, asks it to reorder stops for the selected day, minimising travel time between zones.',
      feat_packing_label: 'Packing mode', feat_packing_desc: 'In Info & Tips, generates a full packing list adapted to your destination, season and activity types.',
      tip1: 'Speak to the AI in any language — it responds in whichever language you use. Be specific: "Add 3 tapas bars near the Gothic Quarter for dinner" works better than "Add some restaurants".',
      warning1: 'The AI requires an OpenAI API key configured on the server. If the AI chat button is greyed out or returns an error, ask your administrator to check the API key.',
    },
    sharing: {
      title: 'Share Trip', subtitle: 'Let anyone view your trip without an account',
      p1: 'The Share Trip feature generates a unique public link that lets anyone — even without an account — view your trip in read-only mode: itinerary, flights, hotels and notes.',
      feat_enable_label: 'Enable sharing', feat_enable_desc: 'In the trip sidebar, click "Share trip" then "Enable sharing". A unique URL is generated immediately.',
      feat_copy_label: 'Copy link', feat_copy_desc: 'Copy the link and send it to family or friends. They can see the full itinerary but cannot edit anything.',
      feat_disable_label: 'Disable sharing', feat_disable_desc: 'Click "Disable sharing" at any time to invalidate the link. Anyone with the old URL will see a "Not found" page.',
      tip1: 'The shared view is a clean, mobile-friendly page with no login required. Perfect for sending the itinerary to a family member who wants to follow your journey.',
      warning1: 'Only the trip owner can enable or disable sharing. Participants with EDITOR or VIEWER roles cannot manage the share link.',
    },
    participants: {
      title: 'Participants', subtitle: 'Invite and manage your travel group',
      p1: 'Invite co-travellers to collaborate on the trip. Each participant has a role that controls what they can do.',
      feat_invite_label: 'Invite by email', feat_invite_desc: 'The invitee must already have an account. Enter their email and choose a role.',
      feat_editor_label: 'EDITOR role', feat_editor_desc: 'Can add, edit and delete content: activities, places, expenses, bookings, tips and checklists.',
      feat_viewer_label: 'VIEWER role', feat_viewer_desc: 'Read-only access. Can see everything but cannot make changes. Good for sharing with someone who wants to follow the plan.',
      tip1: 'Only the trip owner can invite participants, change their role or remove them. The owner is also the only one who can delete the entire trip.',
      step1: 'In the trip sidebar, click the participants button (shows avatars and member count). On mobile, tap the people icon in the trip header bar.',
      step2: 'Click "Invite participant" and enter the email address.',
      step3: 'Choose EDITOR (can edit) or VIEWER (read-only).',
      step4: 'The participant will see the trip in their dashboard immediately.',
    },
    notifications: {
      title: 'Notifications', subtitle: 'In-app reminders for upcoming events',
      p1: 'The app shows in-app reminders when a flight departure or hotel check-in is within the next 48 hours. No account settings needed — it works automatically.',
      feat_flights_label: 'Flight reminders', feat_flights_desc: 'Shown 48h before departure. Urgent (red) within 12 hours.',
      feat_hotels_label: 'Hotel reminders', feat_hotels_desc: 'Shown 48h before check-in. Urgent (red) within 12 hours.',
      feat_where_label: 'Where to find them', feat_where_desc: 'Desktop: amber panel in the trip sidebar. Mobile: red badge on the Bookings tab in the bottom nav.',
      tip1: 'Reminders are calculated from your flights and hotels in the Bookings tab. Make sure to add your actual departure times there to get accurate alerts.',
    },
    community: {
      title: 'Explore Community', subtitle: 'Discover, rate and clone trips from other travellers',
      p1: 'The Explore section is a public feed of trips shared by the Gromly community. Browse destinations, discover itineraries built by real travellers, and clone any trip as a starting point for your own.',
      feat_browse_label: 'Browse & search', feat_browse_desc: 'Filter trips by destination or duration, and sort by Most recent, Top rated or Most used. Use the search bar to find a specific destination.',
      feat_clone_label: 'Clone a trip', feat_clone_desc: 'Click "Use this itinerary" on any community trip to create a full copy in your own dashboard. The clone is independent — your edits do not affect the original.',
      feat_rate_label: 'Rate trips', feat_rate_desc: 'Leave a 1–5 star rating on any published trip. Ratings help surface the best itineraries in the community feed.',
      feat_follow_label: 'Follow travellers', feat_follow_desc: 'Follow other users to filter the community feed to only show their trips. Use the "Following" tab to see their latest publications.',
      tip1: 'To publish your own trip, go to your Dashboard, open the trip menu (three dots) and click "Share to community". The trip will appear in the Explore feed immediately.',
      step1: 'Go to Explore from the top navigation bar.',
      step2: 'Use the search bar or sort options to find trips.',
      step3: 'Click a trip card to view the full itinerary.',
      step4: 'Click "Use this itinerary" to clone it to your dashboard.',
    },
    account: {
      title: 'Account & Security', subtitle: '2FA, privacy controls and your data',
      p1: 'Your account settings are accessible from the user menu in the top-right corner. From there you can manage two-factor authentication, download your data, and delete your account.',
      feat_2fa_label: 'Two-factor authentication', feat_2fa_desc: 'Click "Security" in the user menu to set up 2FA. Scan the QR code with Google Authenticator, Authy or any TOTP app.',
      feat_export_label: 'Download my data', feat_export_desc: 'Click "Download my data" in the user menu to export all your personal data as a JSON file (GDPR Art. 20 — data portability).',
      feat_delete_label: 'Delete account', feat_delete_desc: 'Click "Delete account" in the user menu to permanently delete your account and all associated data. This action cannot be undone.',
      tip1: 'We strongly recommend enabling 2FA — it protects your account even if your password is compromised. Use any TOTP-compatible app: Google Authenticator, Authy, 1Password, etc.',
      warning1: 'Deleting your account is permanent and irreversible. All your trips, expenses, bookings and documents will be deleted. Download your data first if you want a copy.',
    },
    pwa: {
      title: 'Install as App (PWA)', subtitle: 'Use Gromly offline on any device',
      p1: 'Gromly is a Progressive Web App (PWA). You can install it on your phone or desktop and use it like a native app — including when you have no internet connection.',
      feat_ios_label: 'Install on iOS', feat_ios_desc: 'Open Gromly in Safari → tap the Share button → "Add to Home Screen". Done.',
      feat_android_label: 'Install on Android', feat_android_desc: 'Open in Chrome → tap the three-dot menu → "Add to Home screen" or "Install app".',
      feat_desktop_label: 'Install on desktop', feat_desktop_desc: 'In Chrome or Edge, look for the install icon (➕) in the address bar and click it.',
      tip1: "Gromly caches your trips, itinerary and bookings so they load instantly even without a connection. Reading them works offline; adding or editing anything still needs a connection.",
    },
  },
  installPrompt: {
    title: 'Install Gromly',
    subtitle: 'Add it to your home screen for quick, full-screen access.',
    install: 'Install',
    iosIntro: 'To install on your iPhone:',
    iosStep1: 'Tap the Share button',
    iosStep2: 'Choose "Add to Home Screen"',
  },
  maps: {
    quotaExceeded: "You've reached this month's Maps limit. It resets on {{date}}.",
  },
  ai: {
    deleteActivity: 'Delete activity',
    venueNotes: {
      title: 'Rewrite the notes of {{name}}', titleNew: 'Notes for {{name}}',
      before: 'Now', after: 'Proposed', emptied: '(notes left empty)',
    },
    deletePlaces_one: 'Delete place',
    deletePlaces_other: 'Delete {{count}} places',
    deleteAllPlaces_one: 'Empty the places list ({{count}} place)',
    deleteAllPlaces_other: 'Empty the places list ({{count}} places)',
    deleteTakesStops_one: 'It also removes {{count}} stop from the itinerary',
    deleteTakesStops_other: 'It also removes {{count}} stops from the itinerary',
    deleteExpenses_one: 'Delete expense',
    deleteExpenses_other: 'Delete {{count}} expenses',
    deleteAllExpenses_one: 'Empty the expense ledger ({{count}} expense)',
    deleteAllExpenses_other: 'Empty the expense ledger ({{count}} expenses)',
    assistant: 'AI Assistant',
    name: 'gIA',
    expand: 'Expand assistant',
    collapse: 'Collapse assistant',
    saveFailed: 'Could not save',
    someNotSaved: 'Some proposals could not be saved',
    error: 'Sorry, an error occurred. Please try again.',
    clearConversation: 'Clear conversation',
    expenseSplitHint: 'You pay; split equally among everyone. Edit details in Expenses.',
    proposals_one: '{{count}} proposal',
    proposals_other: '{{count}} proposals',
    saving: 'Saving…',
    op: {
      tripDates: {
        title: 'New dates: {{from}} → {{to}}',
        addedDays_one: '{{count}} day is added',
        addedDays_other: '{{count}} days are added',
        removedDays_one: '{{count}} day is removed',
        removedDays_other: '{{count}} days are removed',
        movedStops_one: '{{count}} stop moves to day {{day}}',
        movedStops_other: '{{count}} stops move to day {{day}}',
        removedMeals_one: '{{count}} planned meal is lost',
        removedMeals_other: '{{count}} planned meals are lost',
        removedOutfitDays_one: 'Clothes planned for {{count}} day are lost',
        removedOutfitDays_other: 'Clothes planned for {{count}} days are lost',
        outOfRange_one: '{{count}} booking falls outside the new dates',
        outOfRange_other: '{{count}} bookings fall outside the new dates',
      },
      settleDebt: {
        title: '{{from}} paid {{to}} {{amount}}',
      },
      outfitAdd: {
        title: 'Wear {{item}} on day {{day}}',
      },
      ingredientAdd: {
        title: 'Add {{amount}} {{unit}} of {{name}} to “{{meal}}”',
      },
      mealStop: {
        title: 'Add “{{meal}}” to day {{day}} as a stop',
      },
      duplicate: {
        title_one: 'Copy {{count}} stop to day {{day}}',
        title_other: 'Copy {{count}} stops to day {{day}}',
      },
      item: '– {{text}}',
    },
    cards: {
      empty: '(empty)',
      field: {
        title: 'Title',
        notes: 'Notes',
        startTime: 'Time',
        durationMins: 'Duration (min)',
        name: 'Name',
        category: 'Category',
        customCategory: 'Custom category',
        zone: 'Area',
        priceInfo: 'Price',
        visited: 'Visited',
        airline: 'Airline',
        flightNumber: 'Flight number',
        departureTime: 'Departure',
        arrivalTime: 'Arrival',
        bookingRef: 'Booking ref',
        address: 'Address',
        checkIn: 'Check-in',
        checkOut: 'Check-out',
        bookingUrl: 'Booking link',
        type: 'Type',
        date: 'Date',
        endDate: 'End date',
        endTime: 'End time',
        venue: 'Place',
        description: 'Description',
        amount: 'Amount',
        content: 'Text',
        text: 'Item',
        checked: 'Packed',
        slot: 'Meal',
        bought: 'Bought',
        unit: 'Unit',
      },
      entity: {
        day_one: 'day', day_other: 'days',
        venue_one: 'place', venue_other: 'places',
        flight_one: 'flight', flight_other: 'flights',
        hotel_one: 'stay', hotel_other: 'stays',
        booking_one: 'booking', booking_other: 'bookings',
        expense_one: 'expense', expense_other: 'expenses',
        note_one: 'note', note_other: 'notes',
        tip_one: 'tip', tip_other: 'tips',
        packing_section_one: 'packing section', packing_section_other: 'packing sections',
        packing_item_one: 'packing item', packing_item_other: 'packing items',
        outfit_piece_one: 'planned piece of clothing', outfit_piece_other: 'planned pieces of clothing',
        meal_one: 'meal', meal_other: 'meals',
        grocery_item_one: 'shopping item', grocery_item_other: 'shopping items',
      },
      yes: 'Yes',
      no: 'No',
      editItem: 'Change “{{label}}”',
      deleteItems_one: 'Delete {{count}} item',
      deleteItems_other: 'Delete {{count}} items',
      packed: '✓ {{item}} packed',
      unpacked: '○ {{item}} not packed yet',
      nights_one: '{{count}} night',
      nights_other: '{{count}} nights',
      at: 'At {{place}}',
      editStop: 'Edit “{{title}}”',
      packingItems: 'Packing list',
      note: 'Note',
      day: 'Day {{n}}',
      grocery: 'Shopping list',
      flight: 'Flight',
      stay: 'Accommodation',
      booking: 'Booking',
    },
    acceptAll: 'Accept all',
    accept: 'Accept',
    discard: 'Discard',
    sendHint: 'Enter to send · Shift+Enter new line',
    open: 'Open',
    closed: 'Closed',
    willSaveToPlaces: 'Saved to Places',
    move: 'Move: {{title}}',
    afterStop: 'after {{title}}',
    placeholder: {
      places: '"I want to save a restaurant in the Gothic Quarter"',
      itinerary: '"Add a visit to Sagrada Família on day 2"',
      allroutes: '"Make day 2 a circular route" or "Remove the museum stop"',
      packing: '"Suggest a packing list" or "Add a beach section"',
      tips: '"Give me tips about transport and money"',
      bookings: '"How long between my connecting flights?"',
      expenses: '"How much have we spent on food?" or "Who owes whom?"',
      general: "\"Plan day 3\" or \"What should I pack?\"",
    },
    welcome: {
      places: "Hi! Tell me about places you want to save and I'll suggest them for your Places list.",
      itinerary: "Hi! I can suggest activities for your itinerary or move them. You decide whether to accept.",
      allroutes: "Hi! I can reorder stops to minimise travel, make routes circular, move activities between days or remove stops. You decide what to keep.",
      packing: "Hi! I can suggest a packing list tailored to your trip. You decide whether to accept it.",
      tips: "Hi! I can suggest practical tips for your destination. You decide whether to keep them.",
      bookings: "Hi! Ask me anything about your flights and hotels — times, connections, check-in and logistics.",
      expenses: "Hi! Ask me about your spending, budget and balances — totals by category, who owes whom, or how to split costs.",
      general: "Hi! Ask me anything about your trip — places, the plan, packing, bookings or spending. You decide what to keep.",
    },
    // The vocabulary that unlocks the assistant, made visible: an empty
    // input box teaches nobody what it can do.
    suggestions: {
      general: ["Plan day 1", "Suggest places for my interests", "What should I pack?"],
      places: ["Suggest places for my interests", "Good cheap eats nearby", "Something to do if it rains"],
      itinerary: ["Plan day 1", "This day is too packed", "Add a dinner on day 2"],
      allroutes: ["Optimise this day's route", "Make it circular", "Move this stop to another day"],
      packing: ["What should I pack?", "Anything for the weather?"],
      tips: ["Tips for getting around", "What should I know before going?"],
      bookings: ["When do I land?", "How long is my layover?"],
      expenses: ["What have I spent so far?", "Who owes whom?", "Am I within budget?"],
    },
    category: {
      RESTAURANT: 'Restaurant', CAFE: 'Café', ATTRACTION: 'Attraction',
      VIEWPOINT: 'Viewpoint', NEIGHBORHOOD: 'Neighborhood', PARK: 'Park',
      SHOP: 'Shop', EXPERIENCE: 'Experience', TRANSPORT_HUB: 'Transport',
      BEACH: 'Beach', NATURE: 'Nature', PARKING: 'Parking', OTHER: 'Other',
    },
    context: { places: 'Places', itinerary: 'Itinerary', packing: 'Packing', tips: 'Tips', bookings: 'Bookings', expenses: 'Expenses', general: 'Trip' },
    packingTitle: 'Packing list',
    packingItems_one: '{{count}} item',
    packingItems_other: '{{count}} items',
    packingGoToTab: 'Open the Packing tab',
    budgetTitle: 'Proposed budget',
    dayPlanTitle: 'Day plan',
    dayNumber: 'Day {{number}}',
    showPhotos: 'Show photos',
    activitiesCount_one: '{{count}} activity',
    activitiesCount_other: '{{count}} activities',
    acceptDay: 'Accept day',
    reorderTitle: 'Reorder',
    applyOrder: 'Apply order',
    applying: 'Applying…',
    review: {
      places: 'Review places',
      itinerary: 'Review added activities',
      packing: 'Review packing',
      tips: 'Review tips',
      expenses: 'Review expenses',
    },
    tipsTitle: 'Tips',
    tipsCount_one: '{{count}} tip',
    tipsCount_other: '{{count}} tips',
  },
  services: {
    title: 'Trip services',
    subtitle: 'Hand-picked partners to handle the boring bits before you fly. Activate before you leave — pay with Gromly, keep everything on this trip.',
    partnership: 'Gromly partners.',
    partnershipNote: 'We earn a small commission when you buy through our links — the price you pay stays the same or lower with our code.',
    connectivity: {
      label: 'Connectivity',
      eSIM: 'eSIM',
      coverage: 'Coverage',
      euRoaming: 'EU Roaming',
      heading: 'Stay connected the moment you land',
      body: 'No physical SIM swap. Scan the QR before you fly and your phone picks up data the second your plane touches ground.',
      editorsPick: "Editor's Pick",
      from: 'From',
      viewPlans: 'View plans',
      holafly: {
        subtitle: 'Unlimited data eSIMs',
        description: 'The travel-blog favourite — tap-and-go activation, English-speaking support, 30-day plans for the slow traveller.',
        feature1: 'Unlimited data plans',
        feature2: '24/7 chat in English & ES',
        feature3: '5, 10, 15 or 30 days',
        discountNote: '5% off — use code GROMLY at checkout',
        price: '€19',
        priceNote: 'for 5 days · {{destination}}', priceNoteGeneric: 'for 5 days',
      },
    },
  },
  notes: {
    loadError: 'Failed to load notes', saveError: 'Failed to save note',
    createError: 'Failed to create note', deleteConfirm: 'Delete this note?',
    deleteError: 'Failed to delete note',
    untitled: 'Untitled', empty: 'No content',
    new: 'New', emptyState: 'No notes yet', createFirst: 'Create your first note',
    selectOrCreate: 'Select a note or create a new one',
    allNotes: 'All notes', saved: 'Saved', delete: 'Delete note',
    titleLabel: 'Title', contentLabel: 'Content',
    titlePlaceholder: 'Note title', contentPlaceholder: 'Write your notes here…',
    uploadError: 'Failed to upload file', deleteDocError: 'Failed to delete file',
    attachments: 'Attachments', upload: 'Upload',
    dropHere: 'Click to attach files (images or PDFs)',
  },
  album: {
    ownPhotos: { title: "Your photos for the album", upload: "Upload", hint: "Only in your album: they don’t go to the Gallery and nobody else in the trip sees them.", tripPhotos: "Photos of the trip", remove: "Remove this photo from your album" },
    redoDay: { button: "Redo this day", confirmTitle: "Redo this day from the diary?", confirmBody_one: "Its page is made again with the day’s photos and text. Any change you made to it is lost.", confirmBody_other: "Its {{count}} pages are made again with the day’s photos and text. Any change you made to them is lost." },
    wizard: {
      title_create: "Create your album", title_restart: "Start the album over", steps: "Steps", next: "Next",
      submit_create: "Create album", submit_restart: "Start over", restartWarning: "Every page is made again from the diary. The photos you uploaded to the album stay.",
      format: { step: "Format", title: "Size and shape", body: "You can change it at any time; the pages keep what’s on them." },
      theme: { step: "Style", title: "The album’s style", body: "Paper and colours. The typeface and photo frames are in the editor." },
      density: { step: "Photos", title: "How many photos a day?", body: "The photos you chose in the diary always go in; this only says how many more are added.",
        FEW: "Few", FEW_hint: "Up to 2 a day", NORMAL: "Balanced", NORMAL_hint: "Up to 6 a day", ALL: "All", ALL_hint: "Every photo of each day",
        pages_one: "{{count}} page", pages_other: "{{count}} pages", tooLong: "Over {{max}} pages: the end is cut" },
      cover: { step: "Cover", title: "The cover photo", body: "You can change it later from the cover page." },
    },
    emptyTitle: 'Your album of the trip',
    emptyBody: 'We start it from your diary and your photos, one page per day. Then you choose the layout, the photos and the words. Only you can see it.',
    create: 'Create my album',
    loadError: 'Could not load your album', saveError: 'Could not save the album',
    page: 'Page {{number}}', template: 'Layout', addPage: 'Add page',
    deletePageN: 'Delete page {{number}}',
    deletePage: 'Delete page', deletePageConfirm: 'Delete this page of the album?',
    restart: 'Start over from the diary',
    templates: { COVER: 'Cover', STATS: 'In figures', ONE_PHOTO: 'One photo', TWO_PHOTOS: 'Two photos', THREE_PHOTOS: 'Three photos', FOUR_PHOTOS: 'Four photos', TEXT_PHOTO: 'Text and photo', TRIP_MAP: "Trip map", DAY_MAP: "Day map", TEXT_ONLY: 'Text', FULL_BLEED: 'Full bleed', TWO_PHOTOS_ASYM: 'Large and small', THREE_STRIPS: 'Three strips', FIVE_PHOTOS: 'Five photos', SIX_PHOTOS: 'Six photos', MOSAIC: 'Mosaic', TWO_PHOTOS_TEXT: 'Two photos and text', QUOTE: 'Quote', PANORAMA: 'Panorama', PANORAMA_FRAMED: 'Panorama with margins', TRIP_MAP_SPREAD: 'Trip map, double page', COVER_WRAP: 'Wraparound photo', COVER_FULL: "Full-page photo", COVER_TYPE: "Lettering", COVER_FRAMED: "Framed photo", COVER_MOSAIC: "Four photos", COVER_MAP: "Trip map", COVER_WRAP_FRAMED: "Framed wrap", COVER_WRAP_TWO: "Two photos wrap" },
    spread: { cover: 'Cover', back: 'Back cover', front: 'Front cover', wrap: 'Whole cover', pages: 'Pages {{pages}}', add: 'Add two pages', delete: 'Delete these two pages', deleteN: 'Delete pages {{pages}}', deleteConfirm: 'Delete these two pages of the album?', swap: 'Swap left and right', endpaper: 'Inside of the cover: nothing is printed here', join: 'Join them', joinTitle: 'Join the two pages?', joinBody: 'This layout covers both pages. Whatever does not fit from the facing page will be lost.' },
    spine: { title: 'Spine', placeholder: 'Title on the spine', width: 'About {{mm}} mm wide: the exact width depends on the printer.', tooThin: 'About {{mm}} mm: a title needs {{min}} mm. Add pages to make room for it.' },
    templateGroups: { label: "Jump to layouts", covers: "Covers", '1': '1', '2': '2', '3': '3', '4+': '4+', text: 'Text', map: 'Map', spread: 'Double page' },
    templateSections: { 'covers': "Covers", '1': "One photo", '2': "Two photos", '3': "Three photos", '4+': "Four photos or more", 'text': "Text", 'map': "Maps and figures", 'spread': "Double page" },
    slot: { PHOTO: 'Add a photo', TITLE: 'Add a title', TEXT: 'Add text', MAP: "Choose the day" },
    statsTitle: 'The trip in figures',
    choosePhoto: 'Choose a photo', removePhoto: 'Remove photo', used: 'In use',
    noPhotos: 'This trip has no photos yet. Upload them in the Diary.',
    selectHint: 'Click a box on the page to fill it, or drag a photo onto it.',
    chars: '{{count}} / {{max}}', tooLong: 'Too long for this box: shorten it or pick a layout with more room.',
    fromDiary: 'Insert from the diary', entryOption: 'Day {{number}} · {{text}}',
    dayN: "Day {{n}}", chooseDay: "Choose the day",
    tripMapHint: "The trip map draws itself from your itinerary: every day, in its own colour.",
    figures: { legend: "Legend", map: "Map", world: "World", country: "Country", show: "Show", route: "Route", stops: "Stops only", outline: "Outline", outlineShow: "Show", outlineHide: "Hide", kmCaption: "Kilometres on the road" },
    preview: { open: "Preview", title: "Album preview", prev: "Previous page", next: "Next page" },
    style: { typeface: "Typeface", font: "Font", fontOfAlbum: "The album's", sizeDown: "Smaller", sizeUp: "Bigger", photoFrame: "Photo frame", textSize: "Text size", textAlign: "Alignment", size_S: "Small", size_M: "Medium", size_L: "Large", align_left: "Left", align_center: "Centre", align_right: "Right", theme: "Album theme", pageBackground: "This page's background", fromTheme: "Theme", veil: "Veil", veil_dark: "Dark veil", veil_light: "Light veil", removePhoto: "Remove photo", photoBackground: "Use a photo as background" },
    themes: { paper: "Paper", minimal: "Minimal", night: "Night", sand: "Sand", sage: "Sage", kraft: "Kraft" },
    backgrounds: { paper: "Paper", white: "White", sand: "Sand", sage: "Sage", sky: "Sky", blush: "Blush", kraft: "Kraft", night: "Night", forest: "Forest", ink: "Ink", "paper-dots": "Dotted paper", "paper-grid": "Grid paper", "white-notebook": "Notebook", "sand-stripes": "Striped sand", "sage-dots": "Dotted sage", "night-dots": "Starry night" },
    format: { title: "Size and orientation", "a4-portrait": "A4 portrait", "a5-portrait": "A5 portrait", "a4-landscape": "A4 landscape", "a5-landscape": "A5 landscape", "square-30": "Square 30", "square-20": "Square 20" },
    fontPairs: { classic: "Classic", modern: "Modern", handwritten: "Handwritten", typewriter: "Typewriter", elegant: "Elegant", editorial: "Editorial", rounded: "Rounded", poster: "Poster", script: "Script", retro: "Retro" },
    photoFrames: { none: "None", "white-border": "White border", rounded: "Rounded", shadow: "Shadow" },
    tabs: { page: "Page", album: "Album" },
  },
  journal: {
    views: { list: 'List', map: 'Map' },
    map: { empty: 'No photos on the map yet', emptyBody: 'A photo shows here once it is linked to a stop with a location.', unavailable: 'The map is not available right now.', unplaced_one: '{{count}} photo without a place', unplaced_other: '{{count}} photos without a place' },
    title: 'Diary',
    empty: { title: 'Nothing written yet', body: 'Choose a day and tell what you did, with your photos.' },
    write: 'Write',
    placeholder: 'What did you do today? What will you want to remember?',
    choosePhotos_one: '{{count}} photo chosen',
    choosePhotos_other: '{{count}} photos chosen',
    addPhotos: 'Add photos',
    noPhotosToChoose: 'No photos on this day yet. Add some from here.',
    sharedLabel: 'Followers of the link can see it',
    sharedHint: 'Untick it to keep this entry for the group only.',
    groupOnly: 'Group only',
    groupOnlyHint: 'Followers of the link do not see this entry',
    deleteConfirm: 'Delete this diary entry? Its photos stay in the trip.',
    loadError: 'Could not load the diary',
    saveError: 'Could not save the entry',
    deleteError: 'Could not delete the entry',
    previousPhoto: 'Previous photo',
    nextPhoto: 'Next photo',
  },
  stats: {
    title: 'Your travels',
    km: 'km',
    days_one: 'day', days_other: 'days',
    stops_one: 'stop', stops_other: 'stops',
    photos_one: 'photo', photos_other: 'photos',
    entries_one: 'entry', entries_other: 'entries',
    trips_one: 'trip', trips_other: 'trips',
    nights_one: 'night', nights_other: 'nights',
    countries_one: 'country', countries_other: 'countries',
    regions_one: 'continent', regions_other: 'continents',
    mapLabel_one: 'Map with {{count}} country visited', mapLabel_other: 'Map with {{count}} countries visited',
  },
  gallery: {
    noStop: 'No stop', stopLabel: 'Stop',
    title: 'Gallery', upload: 'Upload photos', uploading: 'Uploading…',
    empty: {
      title: 'No photos yet', dayTitle: 'No photos for this day yet',
      subtitle: 'Drag them here or upload them from your device.',
    },
    allDays: 'All',
    dayOption: 'Day {{number}}', unassignedDay: 'No day', uploadedBy: 'By {{name}}',
    download: 'Download original', delete: 'Delete photo',
    deleteConfirm: 'Delete this photo? This cannot be undone.',
    loadError: 'Failed to load photos', uploadError: 'Failed to upload {{name}}',
    deleteError: 'Failed to delete photo', moveError: 'Failed to move photo',
    selection: { moveToDay: "Move to a day", searchStop: "Search a stop", noStopMatch: "No stop matches", count_one: "{{count}} selected", count_other: "{{count}} selected", all: "Select all", none: "Deselect all", selectPhoto: "Select photo", unselectPhoto: "Deselect photo", place: "Assign place", stopHint: "Photos from another day move to the day of the place you choose.", deleteConfirm_one: "Delete this photo? This cannot be undone.", deleteConfirm_other: "Delete {{count}} photos? This cannot be undone.", updated_one: "{{count}} photo updated", updated_other: "{{count}} photos updated", updateFailed: "{{count}} of {{total}} photos could not be changed", deleted_one: "{{count}} photo deleted", deleted_other: "{{count}} photos deleted", deleteFailed: "{{count}} of {{total}} photos could not be deleted" },
    // ADR-0058
    suggest: { button: "Suggest places", hint: "The AI looks at the photos and suggests the trip stop where each was taken. 1 AI credit per 10 photos.", title: "Places suggested by the AI", looking: "Looking at {{count}} photos…", summary: "Found a stop for {{found}} of {{total}} photos. Tick the ones that are right.", none: "No suggestion", unsure: "Not sure", alreadyThere: "already there", apply: "Apply ({{count}})", privacy: "The photos are sent to OpenAI to analyse them.", failed: "Couldn't suggest places. Try again." },
    placedByDate_one: '{{count}} photo placed on its day by the date it was taken', placedByDate_other: '{{count}} photos placed on their day by the date they were taken',
  },
  billing: {
    credits: { label: 'Trip credits' },
    paywall: {
      title: "Go PRO",
      description: "You've used your free trips. PRO is a yearly plan with unlimited trips and up to 500 AI requests a month.",
      perks: ["Unlimited trips", "500 AI requests a month", "Everything you already use, no limits"],
      cta: "Get PRO",
      error: "Could not start checkout. Please try again.",
    },
    aiPaywall: {
      title: "No AI requests left",
      description: "You've used your free AI requests. PRO includes 500 a month.",
      cta: "Get PRO",
      error: "Could not start checkout. Please try again.",
    },
    payment: {
      success: "Welcome to PRO! It can take a few seconds to show up.",
      cancelled: "Payment cancelled.",
    },
  },
  landing: {
    nav: {
      features: 'Features',
      ai: 'AI',
      community: 'Community',
      marketplace: 'Marketplace',
      signIn: 'Sign in',
      getStarted: 'Get started',
    },
    hero: {
      eyebrow: 'Where every trip begins',
      headline1: 'Plan every',
      headline2: 'adventure.',
      subheading: 'Itineraries, expenses, bookings and places to visit — all in one quiet place, shared only with the people going.',
      pills: {
        itinerary: 'Itinerary',
        expenses: 'Expenses',
        aiRoutes: 'AI Routes',
        plane: 'Plane',
        bookings: 'Bookings',
        collaborative: 'Collaborative',
      },
      ctaPrimary: 'Get started free',
      ctaSecondary: 'Sign in',
    },
    mock: {
      nightsLabel: '{{range}} · {{count}} NIGHTS',
      tripTitle: 'Iceland Summer',
      active: 'ACTIVE',
      moreCount: '+{{count}} more',
      totalSpent: 'TOTAL SPENT',
      ofBudget: 'of {{amount}} budget',
      activities: 'ACTIVITIES',
      acrossDays: 'across {{count}} days',
      dayHeader: 'DAY {{day}} — {{place}}',
      routeOptimized: 'Route optimized',
      splitSettled: 'Split settled · {{count}}p',
      aiOptimized: 'AI Optimized · Day {{day}}',
      whoOwesWhom: 'WHO OWES WHOM',
      settle: 'Settle',
      settledCount: 'SETTLED · {{count}}',
    },
    ticker: {
      label: 'Trips planned in',
    },
    features: {
      eyebrow: 'Everything you need',
      headlineLine1: 'One place for every part',
      headlineLine2Pre: 'of the',
      headlineLine2Highlight: 'trip.',
      description: 'Built for real trips, not idealized ones. Track everything from the flight to the dinner reservation.',
      cards: {
        itinerary: {
          title: 'Day-by-day itinerary',
          desc: 'Build a drag-and-drop itinerary with activities, time slots, and categories. Share it live with everyone on the trip.',
        },
        expenses: {
          title: 'Expenses & splits',
          desc: 'Log every expense, split costs among the crew, and see exactly who owes whom with one tap to settle up.',
        },
        ai: {
          title: 'AI route optimizer',
          desc: 'Tell the AI your preferences and it reorders your stops to minimize travel time. Accepts natural language hints.',
        },
        bookings: {
          title: 'Bookings hub',
          desc: 'Manage flights, hotels, transport and events in one place. Attach PDFs, reference numbers and reminders.',
        },
        map: {
          title: 'Map & venues',
          desc: 'Discover places, mark them visited, and add them to your itinerary directly from the map view.',
        },
        collaborative: {
          title: 'Collaborative planning',
          desc: 'Invite your crew, assign roles, and plan together. Everyone sees changes in real time, no spreadsheets needed.',
        },
      },
    },
    ai: {
      eyebrow: 'Powered by AI',
      headline1: 'Your personal',
      headline2: 'travel optimizer.',
      description: 'Tell the AI your preferences in natural language — meal times, must-see spots, how you like to travel — and it rebuilds your day in seconds.',
      chips: {
        route: 'Route optimization',
        hints: 'Natural language hints',
        mode: 'Travel mode selection',
        grouping: 'Smart grouping by zone',
      },
      cta: 'Try it free',
    },
    groups: {
      eyebrow: 'Built for groups',
      headline1: 'Plan together,',
      headline2: 'not apart.',
      description: 'Invite your crew, assign editors or viewers, and split every expense automatically. One tap to settle up — no awkward WhatsApp messages.',
      features: {
        invite: 'Invite with a link, manage roles',
        split: 'Automatic split calculation',
        settle: 'One-tap debt settlement',
        sync: 'Real-time sync across devices',
      },
      morePlanning: 'planning together',
      morePrefix: '+{{count}} more',
    },
    marketplace: {
      eyebrow: 'Community marketplace',
      headline1: 'Discover trips',
      headline2: 'from the community.',
      description: 'Browse public itineraries from travelers worldwide. Clone any trip with one click and adapt it to your own dates, crew, and budget — no starting from scratch.',
      features: {
        browse: 'Browse curated public itineraries',
        clone: 'Clone and customize any trip',
        ratings: 'Ratings and community reviews',
        follow: 'Follow travelers you admire',
      },
      cta: 'Explore community',
      cloneTrip: 'Clone trip',
      nights: '{{country}} · {{count}} nights',
      tripTags: {
        nature: 'Nature',
        culture: 'Culture',
        adventure: 'Adventure',
        hiking: 'Hiking',
        beach: 'Beach',
        food: 'Food',
        souks: 'Souks',
      },
    },
    bottomCta: {
      eyebrow: 'Start for free',
      headline1: 'Your next adventure',
      headline2: 'starts here.',
      description: 'Join thousands of travelers who plan smarter, split fairer, and remember better.',
      button: 'Create free account',
      haveAccount: 'Already have an account',
      fineprint: 'No credit card required · Free forever plan available',
    },
    footer: {
      privacy: 'Privacy',
      terms: 'Terms',
      legal: 'Legal notice',
      copyright: '© MMXXVI GROMLY · VESTIGI PLATFORM, S.L.',
    },
  },
  explore: {
    toast: {
      loadFailed: 'Failed to load trips',
      tripNotFound: 'Trip not found',
      ratingFailed: 'Rating failed',
      followFailed: 'Failed to update follow',
      cloned: 'Trip added to your account!',
      cloneFailed: 'Clone failed',
      insufficientCredits: 'No trip credits left. Upgrade to get more.',
    },
    breadcrumb: 'Community · Templates',
    heading: 'Explore trips',
    searchPlaceholder: 'Destination or trip…',
    feed: { all: 'All', following: 'Following' },
    filters: {
      days: 'Days', any: 'Any', clear: 'Clear',
      button: 'Filters',
      title: 'Filters',
      sortBy: 'Sort by',
      destination: 'Destination',
      destinationPlaceholder: 'e.g. Paris, Italy…',
      nights: 'Nights',
      budgetPerDay: 'Budget per day',
      budgetHint: 'Based on actual spending divided by trip length.',
      activities: 'Activity types',
      month: 'Travel month',
      anyMonth: 'Any month',
      participants: 'Group size',
      min: 'Min',
      max: 'Max',
      apply: 'Apply',
      reset: 'Reset',
      clearAll: 'Clear all',
      activityType: {
        RESTAURANT: 'Restaurant',
        CAFE: 'Café',
        MONUMENT: 'Monument',
        VIEWPOINT: 'Viewpoint',
        NEIGHBORHOOD: 'Neighborhood',
        PARK: 'Park',
        SHOP: 'Shop',
        EXPERIENCE: 'Experience',
        TRANSPORT: 'Transport',
        PARKING: 'Parking',
        BEACH: 'Beach',
        NATURE: 'Nature',
        ACCOMMODATION: 'Stay',
        OTHER: 'Other',
      },
    },
    sort: { recent: 'Most recent', topRated: 'Top rated', mostUsed: 'Most used' },
    results: {
      trips_one: '{{count}} trip',
      trips_other: '{{count}} trips',
      days_one: '{{count}} day',
      days_other: '{{count}} days',
    },
    empty: {
      title: 'No trips found',
      body: 'Try a different search or filter.',
    },
    loadMore: 'Load more',
    detail: {
      backToExplore: 'Explore',
      mapUnavailable: 'Map not available',
      allPlaces: 'All places',
      dayByDay: 'Day by day',
      dayLabel: 'Day {{n}}',
      stops_one: '{{count}} stop',
      stops_other: '{{count}} stops',
      calculatingRoute: 'Calculating route…',
      noStopsToday: 'No stops with locations on this day.',
      stopFallback: 'Stop {{n}}',
      openInMaps: 'Open in maps',
      noItinerary: 'No itinerary planned.',
      activities_one: '{{count}} activity',
      activities_other: '{{count}} activities',
      noActivities: 'No activities planned',
      noPlaces: 'No places listed.',
      noTips: 'No tips shared.',
      actualSpend: 'Actual spend',
      tripNotes: 'Trip notes',
      useAsTemplate: 'Use as template',
      used: 'Used',
      times_one: '{{count}} time',
      times_other: '{{count}} times',
      communityRating: 'Community rating',
      noRatingsYet: 'No ratings yet',
      ratings_one: '{{count}} rating',
      ratings_other: '{{count}} ratings',
      rateThisTrip: 'Rate this trip',
      youRated_one: 'You rated {{count}} star',
      youRated_other: 'You rated {{count}} stars',
      tripDetails: 'Trip details',
      stats: {
        destination: 'Destination',
        dates: 'Dates',
        duration: 'Duration',
        people: 'People',
        activities: 'Activities',
        nights_one: '{{count}} night',
        nights_other: '{{count}} nights',
      },
      author: 'Author',
      publishedAgo: 'Published {{time}} ago',
      follow: 'Follow',
      following: 'Following',
      travelMode: {
        WALKING: 'Walk',
        DRIVING: 'Drive',
        BICYCLING: 'Bike',
        TRANSIT: 'Transit',
      },
      tabs: {
        itinerary: 'Itinerary',
        places: 'Places',
        map: 'Map',
        tips: 'Tips & Info',
        expenses: 'Budget',
        notes: 'Notes',
      },
    },
    clone: {
      title: 'Use as template',
      tripName: 'Trip name',
      whenLabel: 'When do you want to go?',
      tripEnds: 'Trip ends',
      keepOriginalDates: 'Leave empty to keep the original dates.',
      addToMyTrips: 'Add to my trips',
      daysLabel: 'Days to copy',
      selectAll: 'Select all',
      clearAll: 'Clear all',
      selectAtLeastOne: 'Select at least one day',
      untitledDay: 'Untitled day',
      activitiesCount_one: '{{count}} activity',
      activitiesCount_other: '{{count}} activities',
      summaryDays_one: '{{count}} day',
      summaryDays_other: '{{count}} days',
      summaryNights_one: '{{count}} night',
      summaryNights_other: '{{count}} nights',
    },
  },
  pdf: {
    nights_one: '{{count}} night', nights_other: '{{count}} nights',
    coverLabel: 'Travel itinerary', travellers: 'Travellers', budget: 'Budget', notes: 'Notes',
    generatedOn: 'Generated on {{date}}',
    itinerary: 'Itinerary', placesToVisit: 'Places to visit', bookings: 'Bookings',
    expenses: 'Expenses', checklists: 'Checklists', tips: 'Tips',
    meals: 'Meals', grocery: 'Shopping list',
    flights: 'Flights', hotels: 'Hotels', otherReservations: 'Other reservations',
    balances: 'Balances',
    category: 'Category', name: 'Name', zone: 'Zone', address: 'Address', visited: 'Visited',
    direction: 'Direction', route: 'Route', departure: 'Departure', arrival: 'Arrival',
    flight: 'Flight', ref: 'Ref', outbound: 'Out', return: 'Return',
    type: 'Type', title: 'Title', date: 'Date', description: 'Description',
    paidBy: 'Paid by', amount: 'Amount', owes: 'Owes',
    totalSpent: 'Total spent', perPerson: 'Per person', checkIn: 'Check-in', checkOut: 'Check-out',
  },
  essentials: {
    heading: "Essentials for {{country}}",
    currency: "Currency", language: "Language", capital: "Capital", callingCode: "Calling code",
    plug: "Power", plugType: "Type", driving: "Driving", tapWater: "Tap water", emergency: "Emergency",
    emergencyGeneral: "general", emergencyPolice: "police", emergencyAmbulance: "ambulance", emergencyFire: "fire",
    drive: { left: "Left side", right: "Right side" },
    water: { safe: "Safe to drink", caution: "Be cautious", unsafe: "Drink bottled" },
    advisory: { normal: "No major travel warnings", see_advisory: "Check the travel advisory", avoid_essential: "Avoid all but essential travel", avoid_all: "Avoid all travel" },
    practicalDisclaimer: "General guidance — conditions can vary locally.",
    visa: {
      visa_free: "Visa-free", visa_on_arrival: "Visa on arrival", e_visa: "e-Visa", eta: "Travel authorisation", visa_required: "Visa required", no_admission: "No admission",
      upToDays_one: "up to {{count}} day", upToDays_other: "up to {{count}} days",
    },
    requirements: {
      heading: "Entry requirements", forNationality: "For a {{nationality}} passport",
      forCountry: "Entering {{country}}",
      visa: "Visa", passport: "Passport", vaccines: "Vaccines", unknown: "Not available",
      passportValidity_one: "Valid {{count}}+ month beyond your stay", passportValidity_other: "Valid {{count}}+ months beyond your stay", passportValidZero: "No extra validity required beyond your stay",
      noVaccines: "No specific vaccines required for entry", mandatory: "Required",
      checkedOn: "Checked {{date}}", verifyOfficial: "Verify officially",
      missingPrompt: "Add your nationality to see visa & vaccine requirements", empty: "No entry requirements to show yet",
      disclaimer: "General guidance, not official advice. Entry and health rules change — always confirm with the destination's embassy or an official government source before you travel.",
    },
  },
  redeemCode: {
    entry: "I have a code",
    title: "Open your trip",
    body: "Your travel agency gave you an 8-character code. It works with the email you gave them: {{email}}.",
    label: "Trip code",
    submit: "Open my trip",
    joined: "Your trip is ready",
    errors: {
      notFound: "That code doesn't match an invitation for {{email}}. Check it, and that you signed in with the email you gave your agency.",
      emailNotVerified: "Verify your email first — we sent you a link when you signed up.",
      expired: "This code has expired. Ask your agency for a new one.",
      revoked: "Your agency cancelled this code. Ask them for a new one.",
      tooMany: "Too many attempts. Try again in an hour.",
      generic: "We couldn't open the trip. Try again.",
    },
  },
  agency: {
    card: {
      preparedBy: "Prepared by",
      emergency: "Emergency phone",
      email: "Email",
    },
    managesTravellers: "Your agency manages who travels on this trip. Ask them to add someone.",
    panel: {
      tabs: {
        current: "Trips",
        past: "Past",
        templates: "Circuits",
        clients: "Clients",
        team: "Team",
        settings: "Settings",
      },
      currentTab: "Current",
      newTrip: "New trip",
      newTemplate: "New circuit",
      newDeparture: "New departure",
      duplicate: "Duplicate",
      duplicateName: "{{name}} (copy)",
      duplicateFailed: "Couldn't duplicate the circuit",
      days_one: "{{count}} day",
      days_other: "{{count}} days",
      nights_one: "{{count}} night",
      nights_other: "{{count}} nights",
      addDescription: "Add a description",
      descriptionPlaceholder: "Describe the circuit: what it includes, who it is for…",
      descriptionFailed: "Couldn't save the description",
      empty: {
        currentTitle: "No trips under way",
        currentBody: "Create a trip, or a departure from one of your circuits.",
        pastTitle: "No past trips yet",
        pastBody: "Trips move here a week after they end. From then on they belong to your clients.",
        templatesTitle: "No circuits yet",
        templatesBody: "A circuit is a trip you sell again and again. Prepare it once and create a departure for each date.",
      },
      managedUntil: "Managed until {{date}}",
      handedOver: "Your client's since {{date}}",
      open: "Open",
      lead: "Lead traveller",
      status: {
        NOT_INVITED: "No invitation",
        INVITED: "Invited",
        ACTIVATED: "Activated",
        JOINED: "Joined",
      },
      copyCode: "Copy code",
      codeCopied: "Code copied",
      activations: {
        title: "Activated travellers",
        count_one: "{{count}} traveller activated",
        count_other: "{{count}} travellers activated",
        empty: "Nobody activated in this month.",
        month: "Month",
      },
      usage: {
        title: "This month",
        maps: "Maps: {{used}} of {{cap}}",
        ai: "AI: {{used}} of {{cap}} requests",
      },
      loadFailed: "Could not load the agency",
    },
    home: {
      tagline: "What's on your plate today.",
      stats: {
        ongoing: "Under way",
        leavingSoon: "Leaving in the next 7 days",
        pending: "Travellers not activated yet",
        activated: "Activated this month",
      },
      sections: {
        attention: "Needs attention",
        ongoing: "Under way",
        upcoming: "Next departures",
        activations: "Latest activations",
        circuits: "Circuits",
      },
      attentionHint: "They haven't redeemed their code yet, and the trip is under way or leaves within two weeks.",
      allClear: "Everyone's in. Nothing needs you right now.",
      leavesToday: "Leaves today",
      leavesTomorrow: "Leaves tomorrow",
      leavesIn_one: "Leaves in {{count}} day",
      leavesIn_other: "Leaves in {{count}} days",
      dayOf: "Day {{day}} of {{total}}",
      activatedOf: "{{done}}/{{total}} activated",
      noUpcoming: "No departures scheduled.",
      noActivations: "Nobody activated this month yet.",
      allTrips: "All trips",
      seeAll: "See all",
      emptyTitle: "Your agency is ready",
      emptyBody: "Create your first trip, or a circuit you'll sell again and again.",
    },
    departure: {
      title: "New departure: {{name}}",
      name: "Trip name",
      startDate: "Start date",
      travellers: "Travellers",
      leadHint: "The first one owns the trip once they redeem their code.",
      travellerName: "Name",
      travellerEmail: "Email (to send the code)",
      addTraveller: "Add traveller",
      removeTraveller: "Remove",
      submit: "Create departure",
      created: "Departure created",
      invitesFailed_one: "{{count}} invitation couldn't be sent",
      invitesFailed_other: "{{count}} invitations couldn't be sent",
      failed: "Could not create the departure",
    },
    settings: {
      brandTitle: "What your travellers see",
      brandHint: "Shown inside the trip while you manage it.",
      name: "Agency name",
      emergencyPhone: "Emergency phone",
      contactEmail: "Contact email",
      logo: "Logo",
      uploadLogo: "Upload logo",
      removeLogo: "Remove logo",
      save: "Save",
      saved: "Saved",
      saveFailed: "Could not save",
    },
    clients: {
      title: "Clients",
      new: "New client",
      edit: "Edit client",
      search: "Search by name or email",
      saveFailed: "Could not save the client",
      loadFailed: "Could not load the client",
      deleteFailed: "Could not delete the client",
      form: {
        name: "Name",
        email: "Email",
        phone: "Phone",
        birthDate: "Date of birth",
        notes: "Notes",
        emailInvalid: "That email doesn't look right",
        emailHint: "Without an email they still travel, but get no code: someone else in the group can share the trip with them.",
      },
      picker: {
        noEmail: "No email — won't get a code",
        makeLead: "Make lead traveller",
        remove: "Remove",
        search: "Search your clients",
        noMatch: "No client matches.",
        emptyBook: "Your client book is empty — add the first one.",
        createNamed: "New client «{{name}}»",
      },
      bornOn: "Born {{date}}",
      hasAccount: "Has an account",
      trips: "Trips with you",
      noTrips: "No trips yet.",
      deleteTitle: "Delete {{name}}?",
      deleteBody: "They leave your client book. Their trips stay theirs.",
      noMatchTitle: "No client matches",
      emptyTitle: "No clients yet",
      emptyBody: "Every traveller you add to a trip lands here, so next time you just pick them.",
      noContact: "No contact details",
      tripCount_one: "{{count}} trip",
      tripCount_other: "{{count}} trips",
    },
    team: {
      loadFailed: "Could not load the team",
      lastAdmin: "The agency needs at least one admin.",
      failed: "Something went wrong",
      invited: "Invitation sent to {{email}}",
      alreadyMember: "They're already on the team.",
      inviteTitle: "Invite to the team",
      inviteHint: "They'll get an email and join once they accept with that address. Their own trips stay theirs.",
      emailPlaceholder: "colleague@agency.com",
      role: "Role",
      roles: {
        ADMIN: "Admin",
        AGENT: "Agent",
      },
      invite: "Invite",
      you: "(you)",
      remove: "Remove {{name}}",
      pending: "Pending",
      revoke: "Cancel invitation",
      removeTitle: "Remove {{name}} from the team?",
      removeBody: "They lose access to the agency's trips and clients. Their own trips stay theirs.",
      removeConfirm: "Remove",
    },
    invite: {
      gone: {
        expired: "This invitation has expired",
        used: "This invitation has already been used",
        notFound: "This invitation doesn't exist",
      },
      goneBody: "Ask the agency to send you a new one.",
      goHome: "Go to my home",
      accepted: "Welcome to {{agency}}",
      verifyFirst: "Verify your email first, then accept.",
      failed: "Could not accept the invitation",
      eyebrow: "Invitation",
      title: "Join {{agency}}",
      body: {
        ADMIN: "As an admin: you prepare trips for its clients and manage the team.",
        AGENT: "You'll prepare trips for its clients.",
      },
      keepTrips: "Your own trips stay yours: you switch between them and the agency from your profile menu.",
      accept: "Accept and join",
      wrongEmail: "This invitation is for {{invited}}, and you are signed in as {{current}}. Sign in with that email to accept it.",
    },
    wizard: {
      ask: "Who is this trip for?",
      hint: "Pick them from your clients, or add a new one. The first is the lead traveller: the trip is theirs once they redeem their code.",
      clientsCount_one: "{{count}} client",
      clientsCount_other: "{{count}} clients",
    },
  },
  admin: {
    loadFailed: "Couldn't load this. Try again.",
    acting: { inside: "You're inside {{agency}} as Gromly", leave: "Leave", enter: "Enter" },
    usage: {
      subtitle: "{{from}} – {{to}}, compared with {{prevFrom}} – {{prevTo}}.", subtitleLoading: "Loading…",
      rangeLabel: "Period", from: "From", to: "To",
      range: { month: "This month", lastMonth: "Last month", "7d": "7 days", "30d": "30 days", "90d": "90 days", "12m": "12 months", custom: "Custom" },
      segmentLabel: "Whose spend", segment: { all: "Everything", personal: "Personal trips", agency: "Agency trips" },
      clearFilter: "Stop filtering by {{name}}", deletedUser: "Deleted account", deletedTrip: "Deleted trip",
      vsPrevious: "Against the previous period",
      tiles: { total: "Total", maps: "Maps", ai: "AI", users: "Users with spend", outside: "Outside trips", projection: "Month projection" },
      calls_one: "{{count}} call", calls_other: "{{count}} calls",
      aiDetail: "{{calls}} calls · {{credits}} credits", perUser: "{{amount}} per user",
      outsideDetail: "Search and the wizard, before a trip exists", projectionDetail: "At this month's pace",
      trend: "Spend over the period", trendLabel: "Maps and AI spend over the period",
      byMaps: "Maps by service", byAi: "AI by feature", byModel: "AI by model",
      topUsers: "Who spends most", topTrips: "Trips that cost most", noAgencySpend: "No agency spend in this period",
      split: "Maps {{maps}} · AI {{ai}}", mapsCap: "Maps cap {{amount}}/month",
      empty: "No spend in this period",
      footnote: "Recorded from 27 July 2026. Spend inside an agency trip counts for the agency (ADR-0034); spend outside any trip, as personal.",
      maps: { placeResolution: "Finding places", placeDetails: "Place details", photos: "Photos", search: "Search and autocomplete", routes: "Routes", mapLoads: "Map loads", other: "Other" },
      ai: { chatQuestion: "Chat · questions", chatAction: "Chat · actions", chatTriage: "Chat · triage", chat: "Chat · other", import: "Document import", extraction: "Ticket and receipt reading", wizard: "New trip wizard", essentials: "Trip essentials" },
    },
    nav: { overview: "Overview", users: "Users", agencies: "Agencies", usage: "Usage & costs" },
    overview: {
      title: "Gromly at a glance", subtitle: "Who uses it, where they come from and what it costs.",
      signups: "Sign-ups per month", trips: "Trips created per month", seeUsers: "See users",
      personalTrips: "Personal", agencyTrips: "With an agency", manageAgencies: "Manage agencies",
      footnote: "Active users count from the day activity started being recorded. Demo trips and agency templates are not counted as trips.",
      chartLabel: "Monthly chart: {{series}}",
      activations_one: "{{count}} traveller activated this month", activations_other: "{{count}} travellers activated this month",
      maps: "Maps this month", ai: "AI requests",
      stats: {
        users: "Users", usersDetail_one: "{{count}} new this month", usersDetail_other: "{{count}} new this month",
        active: "Active (30 days)", activeDetail_one: "{{count}} in the last 7 days", activeDetail_other: "{{count}} in the last 7 days",
        pro: "PRO", proDetail_one: "{{count}} as a gift", proDetail_other: "{{count}} as a gift",
        fromAgencies: "Came through agencies", fromAgenciesDetail_one: "{{count}} then planned a trip of their own", fromAgenciesDetail_other: "{{count}} then planned a trip of their own",
        trips: "Trips", tripsDetail_one: "{{count}} this month · {{agency}} with an agency", tripsDetail_other: "{{count}} this month · {{agency}} with an agency",
        cost: "Cost this month", costDetail: "Last month: {{amount}}",
      },
    },
    users: {
      total_one: "{{count}} user", total_other: "{{count}} users",
      search: "Search by name, username or email", originLabel: "Origin", anyOrigin: "Any origin", tagLabel: "Type", anyTag: "Any type",
      empty: "No users match these filters", pagination: "Pages", prev: "Previous page", next: "Next page", pageOf: "Page {{page}} of {{pages}}",
      origin: { direct: "Direct", agency: "Through an agency" },
      filter: { staff: "Agency staff", traveller: "Agency travellers", pro: "PRO", admin: "Admins" },
      tags: { staffOf: "Staff · {{agency}}", travellerOf: "Traveller · {{agency}}", pro: "PRO", proGift: "PRO gift", admin: "Admin" },
      cols: { user: "User", tags: "Profile", trips: "Trips", joined: "Joined", lastActive: "Last active" },
    },
    user: {
      ownTrips: "Own trips", agencyTrips: "Trips with an agency", usageMonth: "Usage this month", usageAll: "Usage, all time",
      aiCredits_one: "{{count}} AI credit", aiCredits_other: "{{count}} AI credits",
      credits: "Credits left", creditsValue: "{{ai}} AI · {{trips}} trips",
      pro: {
        title: "PRO", paid: "Pays for PRO ({{source}}) until {{date}}. A paid PRO can't be gifted over or withdrawn here.",
        giftUntil: "PRO gift until {{date}}.", none: "Not PRO. You can gift it until a date.",
        presets: "Duration", months_one: "{{count}} month", months_other: "{{count}} months",
        extendMonths_one: "+{{count}} month", extendMonths_other: "+{{count}} months",
        until: "Until", grant: "Gift PRO", update: "Change date", revoke: "Withdraw gift",
        revokeTitle: "Withdraw the PRO gift?", revokeBody: "They stop being PRO right now. Nothing they created is deleted.",
        granted: "PRO gifted", revoked: "Gift withdrawn", alreadyPaid: "This user already pays for PRO", failed: "Couldn't save the change",
        historyGranted: "{{date}} · gifted by {{actor}}", historyRevoked: "{{date}} · withdrawn by {{actor}}", unknownActor: "a deleted admin",
        source: { STRIPE: "web", APP_STORE: "App Store", PLAY_STORE: "Google Play" },
      },
    },
  },
  adminAgencies: {
    title: "Agencies",
    name: "Name",
    contactEmail: "Contact email",
    members: "Staff",
    addMember: "Invite",
    memberEmail: "Email of the agency admin",
    removeMember: "Remove",
    mapsCap: "Maps cap (€/month)",
    aiCap: "AI requests / month",
    activations_one: "{{count}} activated this month",
    activations_other: "{{count}} activated this month",
    downloadCsv: "Activations CSV",
    save: "Save",
    saved: "Saved",
    empty: "No agencies yet.",
    invited: "Invitation sent to {{email}}",
    alreadyMember: "Already on the team.",
    failed: "Something went wrong",
    subtitle: "Agencies are signed up by hand. Their staff need a Gromly account first.",
    newTitle: "New agency",
    staffEmpty: "No staff yet: add the email of someone with a Gromly account.",
    limitsTitle: "Monthly ceilings",
    limitsHint: "What the agency's trips can spend on Maps and AI, all of them together.",
    billingTitle: "Invoice",
    billingHint: "One line per traveller activated in the month.",
    statMaps: "Maps this month",
    statAi: "AI this month",
    statActivations: "Activated this month",
    of: "{{used}} of {{cap}}",
    logoHint: "Click the logo to change it",
  },
} as const;

export default en;
export type Translation = typeof en;
